import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import socket, { joinClienteRooms } from 'shared/socket';
import axios from 'axios';
import MapaUbicacion from 'shared/components/MapaUbicacion';
import { getImagenUrl, getSchemaName } from 'shared/api';
import { fmtMonto } from 'shared/format';
import { etiquetaTotal } from 'shared/etiquetaUtils';
import {
  FiArrowLeft,
  FiPackage,
  FiClock,
  FiSearch,
  FiDollarSign,
  FiCheckCircle,
  FiTruck,
  FiFlag,
  FiXCircle,
  FiPlus,
  FiMinus,
  FiGrid,
  FiTrash2,
  FiMessageSquare,
  FiBox,
  FiEdit3,
  FiMapPin,
  FiUpload,
  FiCreditCard,
  FiCopy,
} from 'react-icons/fi';
import { useNotificaciones } from 'shared/context/NotificacionesContext';
import useWindowSize from 'shared/hooks/useWindowSize';
import SelectorPedidos from './SelectorPedidos';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const FASES_CONTADO = [
  { key: 'pendiente', label: 'Creado', Icon: FiClock },
  { key: 'en_revision', label: 'Revision', Icon: FiSearch },
  { key: 'aprobado', label: 'Pago', Icon: FiDollarSign },
  { key: 'pagado', label: 'Pagado', Icon: FiCheckCircle },
  { key: 'armado', label: 'Armando', Icon: FiPackage },
  { key: 'listo', label: 'Listo', Icon: FiBox },
  { key: 'en_camino', label: 'En Camino', Icon: FiTruck },
  { key: 'entregado', label: 'Entregado', Icon: FiFlag },
];

const FASES_CREDITO = [
  { key: 'pendiente', label: 'Creado', Icon: FiClock },
  { key: 'en_revision', label: 'Revision', Icon: FiSearch },
  { key: 'armado', label: 'Armando', Icon: FiPackage },
  { key: 'listo', label: 'Listo', Icon: FiBox },
  { key: 'en_camino', label: 'En Camino', Icon: FiTruck },
  { key: 'entregado', label: 'Entregado', Icon: FiFlag },
  { key: 'pagado', label: 'Pagado', Icon: FiCheckCircle },
];

const ESTADO_IDX = [
  'pendiente',
  'en_revision',
  'aprobado',
  'esperando_pago',
  'pago_en_verificacion',
  'pagado',
  'armado_cocina',
  'armado',
  'listo',
  'despachado',
  'en_camino',
  'entregado',
];

const ESTADO_COLOR = {
  pendiente: '#FF9500',
  en_revision: '#FF9500',
  aprobado: 'var(--color-secondary)',
  esperando_pago: '#FF9500',
  pago_en_verificacion: '#FF9500',
  pagado: 'var(--color-success)',
  armado_cocina: '#ff6d00',
  armado: '#ff6d00',
  listo: 'var(--color-secondary)',
  despachado: '#00bcd4',
  en_camino: '#00bcd4',
  llegada: '#009688',
  entregado: 'var(--color-success)',
  rechazado: 'var(--color-danger)',
  cancelado: '#9aa0a6',
  credito_pagado: 'var(--color-success)',
};

const ESTADO_LABEL = {
  pendiente: 'Pendiente',
  en_revision: 'En Revisión',
  aprobado: 'Método de Pago',
  esperando_pago: 'Factura',
  pago_en_verificacion: 'Verificando',
  pagado: 'Factura',
  armado_cocina: 'Armando',
  armado: 'Armando',
  listo: 'Listo',
  despachado: 'Despachado',
  en_camino: 'En Camino',
  llegada: 'Llegada',
  entregado: 'Entregado',
  rechazado: 'Rechazado',
  cancelado: 'Cancelado',
  credito_pagado: 'Credito pagado',
};

const TIPO_ENTREGA_LABEL = {
  retiro: 'Retiro en tienda',
  delivery: 'Delivery a domicilio',
  encomienda: 'Encomienda',
};
const TIPO_ENTREGA_ICON = { retiro: FiBox, delivery: FiTruck, encomienda: FiPackage };

function ClientePedidoDetalle({
  pedidoId: propPedidoId,
  inline = false,
  config,
  onBack,
  onGoToChat,
  onGoToCatalog,
  abrirConfirmacionAlMontar = false,
  onConfirmacionConsumida,
  pedidosActivos = [],
  pedidoSeleccionadoId,
  onSeleccionarPedido,
  sessionId,
}) {
  const pedidoId = propPedidoId;
  const navigate = useNavigate();
  const schema_name = getSchemaName();
  const [pedido, setPedido] = useState(null);
  const pva = config?.permitir_venta_agotados || false;
  const [cargando, setCargando] = useState(true);
  const [metodoPago, setMetodoPago] = useState('');
  const [errorCarga, setErrorCarga] = useState(null);

  const abortControllerRef = useRef(null);

  const cargarPedido = useCallback(async (silent = false) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const ac = new AbortController();
    abortControllerRef.current = ac;
    if (!silent) setCargando(true);
    setErrorCarga(null);
    try {
      const params = sessionId ? `?schema_name=${schema_name}&session_id=${sessionId}` : `?schema_name=${schema_name}`;
      const res = await axios.get(`${API_URL}/pedidos/${pedidoId}${params}`, {
        signal: ac.signal,
      });
      if (res.data.status === 'success') {
        setPedido(res.data.data);
        const p = res.data.data;
        if (p.codigo_sede && p.productos) {
          let prods = p.productos;
          if (typeof prods === 'string') { try { prods = JSON.parse(prods); } catch { prods = []; } }
          const codigos = [...new Set((prods || []).map(item => item.codigo).filter(Boolean))];
          if (codigos.length > 0) {
            try {
              const stockRes = await axios.get(
                `${API_URL}/pedidos/productos/cliente-buscar?q=${encodeURIComponent(codigos.join(' '))}&sede=${p.codigo_sede}&limite=${codigos.length + 5}&schema_name=${encodeURIComponent(schema_name)}`
              );
              if (stockRes.data.status === 'success') {
                const map = {};
                (stockRes.data.data || []).forEach(item => { map[item.codigo] = item.stock ?? 0; });
                setStockCache(map);
              }
            } catch { console.error('Error obteniendo stock para productos del pedido'); }
          }
        }
        if (socketRef.current && res.data.data.idcliente) {
          joinClienteRooms(socketRef.current, {
            idcliente: res.data.data.idcliente,
            empresaId: res.data.data.empresa_id || res.data.data.idempresa,
            sessionId,
          });
        }
      }
    } catch (e) {
      if (e?.code !== 'ERR_CANCELED') {
        console.error('Error:', e);
        setErrorCarga('Error al cargar el pedido. Verifica tu conexión.');
      }
    } finally {
      if (!silent) setCargando(false);
    }
  }, [pedidoId, schema_name, sessionId]);

  useEffect(() => {
    if (pedidoId) cargarPedido();
  }, [pedidoId, cargarPedido]);
  const [subiendoComp, setSubiendoComp] = useState(false);
  const [confirmarCancelar, setConfirmarCancelar] = useState(false);
  const [confirmarPedido, setConfirmarPedido] = useState(false);
  const [productoSeleccionado, setProductoSeleccionado] = useState(null);
  const [stockCache, setStockCache] = useState({});
  const [tasa, setTasa] = useState(0);
  const [metodosPago, setMetodosPago] = useState([]);
  const [datosCliente, setDatosCliente] = useState({});
  const [compFile, setCompFile] = useState(null);
  const [compPreview, setCompPreview] = useState(null);
  const [metodoSeleccionado, setMetodoSeleccionado] = useState(null);
  const [modalAlerta, setModalAlerta] = useState('');
  const [cambiandoMetodo, setCambiandoMetodo] = useState(false);
  const [reintentandoPago, setReintentandoPago] = useState(false);
  const [pagoMonto, setPagoMonto] = useState('');
  const [pagoMontoCustom, setPagoMontoCustom] = useState(false);
  const [pagoMetodoId, setPagoMetodoId] = useState('');
  const [pagoMetodoSel, setPagoMetodoSel] = useState(null);
  const [pagoArchivo, setPagoArchivo] = useState(null);
  const [pagoPreview, setPagoPreview] = useState(null);
  const [agregandoPago, setAgregandoPago] = useState(false);
  const [mostrarAgregarPago, setMostrarAgregarPago] = useState(false);
  const [pasoPago, setPasoPago] = useState(1);
  const [copiado, setCopiado] = useState(false);
  const [tiposDelivery, setTiposDelivery] = useState([]);
  const [tipoEntrega, setTipoEntrega] = useState('');
  const [condicionVenta, setCondicionVenta] = useState('credito');
  const [mostrarCambiarDir, setMostrarCambiarDir] = useState(false);
  const [direccionesCliente, setDireccionesCliente] = useState([]);
  const [cargandoDirLista, setCargandoDirLista] = useState(false);
  const [cambiandoDir, setCambiandoDir] = useState(false);
  const sessionIdRef = useRef(null);
  const socketRef = useRef(null);
  const pedidoRef = useRef(null);
  const { agregarNotificacion } = useNotificaciones();

  useEffect(() => { pedidoRef.current = pedido; }, [pedido]);
  const { width } = useWindowSize();
  const isDesktop = width >= 768;

  useEffect(() => {
    if (
      pedido &&
      (pedido.estado === 'entregado' ||
        pedido.estado === 'rechazado' ||
        pedido.estado === 'cancelado' ||
        pedido.estado === 'credito_pagado')
    ) {
      if (inline && onBack) {
        onBack();
      } else if (!inline) {
        navigate('/', { replace: true });
      }
    }
  }, [pedido, inline, onBack, navigate]);

  useEffect(() => {
    if (
      abrirConfirmacionAlMontar &&
      pedido &&
      pedido.estado === 'pendiente' &&
      pedido.tipo_pedido !== 'corporativo'
    ) {
      setConfirmarPedido(true);
      if (onConfirmacionConsumida) onConfirmacionConsumida();
    }
    // eslint-disable-next-line
  }, [abrirConfirmacionAlMontar, pedido]);

  useEffect(() => {
    const params = {};
    if (schema_name) params.schema_name = schema_name;
    axios
      .get(`${API_URL}/pedidos/config/tasa`, { params })
      .then((r) => {
        if (r.data.status === 'success') setTasa(parseFloat(r.data.data.tasa_cambio) || 0);
      })
      .catch((e) => console.error('Error cargando tasa:', e?.message || e));
  }, [schema_name]);

  useEffect(() => {
    const slug = (schema_name || '').replace(/_/g, '-');
    if (!slug) return;
    axios
      .get(`${API_URL}/publico/empresa-config`, { params: { slug } })
      .then((r) => {
        if (r.data.status === 'success') {
          const td = r.data.data.tipos_delivery || [];
          setTiposDelivery(td);
          if (td.length === 1) setTipoEntrega(td[0]);
          setCondicionVenta(r.data.data.credito_habilitado ? 'credito' : 'contado');
        }
      })
      .catch(() => {});
  }, [schema_name]);

  useEffect(() => {
    if (pedido && pedido.estado === 'esperando_pago') {
      axios
        .get(`${API_URL}/pedidos/${pedido.id}/metodos-pago`, { params: { schema_name } })
        .then((r) => {
          if (r.data.status === 'success') setMetodosPago(r.data.data);
        })
        .catch((e) => console.error('Error cargando métodos de pago:', e?.message || e));
    }
  }, [pedido, schema_name]);

  useEffect(() => {
    if (!productoSeleccionado?.codigo || !pedido?.codigo_sede) return;
    let cancelled = false;
    axios
      .get(`${API_URL}/catalogo/producto/${productoSeleccionado.codigo}?sede=${pedido.codigo_sede}&schema_name=${schema_name}`)
      .then((res) => {
        if (!cancelled && res.data.status === 'success' && res.data.data?.etiquetas) {
          setProductoSeleccionado((prev) => {
            if (!prev || prev.codigo !== productoSeleccionado.codigo) return prev;
            return { ...prev, _etiquetasDisponibles: res.data.data.etiquetas };
          });
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [productoSeleccionado?.codigo, productoSeleccionado?.nombre, pedido?.codigo_sede, schema_name]);

  useEffect(() => {
    socketRef.current = socket;

    const onConnect = () => {
      const p = pedidoRef.current;
      if (p?.idcliente) {
        joinClienteRooms(socket, {
          idcliente: p.idcliente,
          empresaId: p.empresa_id || p.idempresa,
          sessionId,
        });
      }
      cargarPedido(true);
    };
    const onPedidoActualizado = (data) => {
      if (data.pedido_id === parseInt(pedidoId)) {
        if (data.estado || data.total !== undefined || data.productos) {
          setPedido(prev => prev ? {
            ...prev,
            estado: data.estado || prev.estado,
            total: data.total !== undefined ? data.total : prev.total,
            productos: data.productos || prev.productos,
          } : null);
        }
        cargarPedido(true);
      }
      if (data.confirmacion_pendiente) {
        agregarNotificacion({
          tipo: 'confirmacion',
          titulo: 'Confirma tu pedido',
          mensaje: `Revisa los cambios de tu pedido.`,
          pedidoId: data.pedido_id,
        });
      }
    };
    const onNuevoMensaje = (data) => {
      const p = pedidoRef.current;
      if (p && parseInt(data.idcliente) === parseInt(p.idcliente)) cargarPedido(true);
      if (data.autor === 'asesor' || data.autor === 'sistema') {
        agregarNotificacion({
          tipo: 'mensaje',
          titulo: data.autor === 'sistema' ? 'Pedido actualizado' : 'Nuevo mensaje',
          mensaje: data.mensaje?.substring(0, 80) || '',
          pedidoId: data.pedido_id,
        });
      }
    };
    const onUbicacionDelivery = (data) => {
      if (data.pedido_id === parseInt(pedidoId))
        setPedido((prev) =>
          prev ? { ...prev, lat_delivery: data.lat, lng_delivery: data.lng } : prev
        );
    };
    if (!socket.connected) socket.connect();
    socket.on('connect', onConnect);
    socket.on('pedido_actualizado', onPedidoActualizado);
    socket.on('nuevo_mensaje', onNuevoMensaje);
    socket.on('ubicacion_delivery', onUbicacionDelivery);
    if (socket.connected) onConnect();
    return () => {
      socket.off('connect', onConnect);
      socket.off('pedido_actualizado', onPedidoActualizado);
      socket.off('nuevo_mensaje', onNuevoMensaje);
      socket.off('ubicacion_delivery', onUbicacionDelivery);
    };
  }, [pedidoId, sessionId, cargarPedido, agregarNotificacion]);

  const cancelarPedido = async () => {
    if (!pedido) return;
    try {
      let res;
      if (pedido.tipo_pedido === 'corporativo' && pedido.grupo_cobro) {
        res = await axios.put(
          `${API_URL}/pedidos/corporativo/${pedido.grupo_cobro}/cancelar?schema_name=${schema_name}&session_id=${sessionId || ''}`
        );
      } else {
        res = await axios.put(
          `${API_URL}/pedidos/${pedido.id}/cancelar?schema_name=${schema_name}`
        );
      }
      if (res.data.status === 'error') {
        alert(res.data.message || 'No se pudo cancelar el pedido');
      } else {
        setPedido((prev) => ({ ...prev, estado: 'cancelado' }));
      }
    } catch (err) {
      alert('Error de conexión al intentar cancelar');
    }
  };

  const submitPago = async () => {
    if (!pagoMetodoId) {
      setModalAlerta('Selecciona un método de pago');
      return;
    }
    const requiereComp = pagoMetodoSel?.requiere_comprobante !== false;
    if (requiereComp && !pagoArchivo) {
      setModalAlerta('Adjunta tu comprobante de pago para continuar');
      return;
    }
    setAgregandoPago(true);
    try {
      const tasa = pagoMetodoSel?.tasa || 1;
      const montoLocal = parseFloat(pagoMonto);
      const montoUSD = (montoLocal / tasa).toFixed(2);
      const fd = new FormData();
      fd.append('metodo_pago_id', pagoMetodoId);
      fd.append('schema_name', schema_name);
      fd.append('session_id', sessionId || '');
      fd.append('monto', montoUSD);
      if (pagoArchivo) fd.append('archivo', pagoArchivo);
      const res = await axios.post(`${API_URL}/pedidos/${pedido.id}/pagos`, fd);
      if (res.data.status === 'success') {
        setPagoMetodoId('');
        setPagoMetodoSel(null);
        setPagoMonto('');
        setPagoMontoCustom(false);
        setPagoArchivo(null);
        setPagoPreview(null);
        setMostrarAgregarPago(false);
        setPasoPago(1);
        cargarPedido();
      } else {
        setModalAlerta(res.data.message || 'Error al agregar el pago');
      }
    } catch (e) {
      setModalAlerta(e?.response?.data?.detail || 'Error al agregar el pago');
    }
    setAgregandoPago(false);
  };

  const copiarDatosPago = async () => {
    const m = pagoMetodoSel;
    if (!m) return;
    // pagoMonto ya está en la moneda local del método (Bs/COP/USD).
    const montoLocal = parseFloat(pagoMonto || 0).toFixed(2);
    const lineas = [];
    lineas.push(`Método: ${m.nombre}`);
    lineas.push(`Monto: ${m.simbolo || '$'}${montoLocal}`);
    const datos = m.datos_negocio?.datos_mostrar;
    if (Array.isArray(datos) && datos.length > 0) {
      datos.forEach((d) => {
        if (d && d.etiqueta && d.valor != null) lineas.push(`${d.etiqueta}: ${d.valor}`);
      });
    }
    const texto = lineas.join('\n');
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(texto);
      } else {
        const ta = document.createElement('textarea');
        ta.value = texto;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch {
      setModalAlerta('No se pudo copiar. Copia manualmente los datos.');
    }
  };

  const ejecutarConfirmar = async () => {
    if (tiposDelivery.length > 0 && !tipoEntrega) {
      setModalAlerta('Selecciona el método de entrega antes de confirmar');
      return;
    }
    try {
      if (pedido.tipo_pedido === 'corporativo') {
        // Confirmacion a nivel de grupo: un solo tipo_entrega para todas las sedes.
        await axios.put(`${API_URL}/pedidos/corporativo/${pedido.grupo_cobro}/confirmar`, {
          schema_name: schema_name,
          tipo_entrega: tipoEntrega || null,
          session_id: sessionId || null,
        });
      } else {
        await axios.put(`${API_URL}/pedidos/${pedido.id}/confirmar`, {
          schema_name: schema_name,
          tipo_entrega: tipoEntrega || null,
          condicion_venta: condicionVenta,
        });
      }
      setPedido((prev) => ({
        ...prev,
        estado: 'en_revision',
        tipo_entrega: tipoEntrega || prev?.tipo_entrega,
        condicion_venta: pedido.tipo_pedido === 'corporativo' ? 'credito' : condicionVenta,
      }));
      setConfirmarPedido(false);
                      } catch (e) {
                        const msg = e.response?.data?.message || 'Error al guardar cambios';
                        alert(msg);
                      }
  };

  const abrirCambiarDireccion = async () => {
    setMostrarCambiarDir(true);
    setCargandoDirLista(true);
    try {
      const sid = pedido?.session_id || sessionIdRef.current;
      const slug = (schema_name || '').replace(/_/g, '-');
      if (!sid || !slug) return;
      const res = await axios.get(`${API_URL}/perfil-cliente/${schema_name}/${sid}`, {
        headers: { 'x-empresa-slug': slug },
      });
      if (res.data.status === 'success') {
        const dirs = (res.data.data.direcciones || []).filter((d) => d.lat && d.lng);
        setDireccionesCliente(dirs);
      }
    } catch {
    } finally {
      setCargandoDirLista(false);
    }
  };

  const ejecutarCambiarDireccion = async (direccionId) => {
    setCambiandoDir(true);
    try {
      const res = await axios.put(`${API_URL}/pedidos/${pedido.id}/cambiar-direccion`, {
        schema_name: schema_name,
        direccion_id: direccionId,
      });
      if (res.data.status === 'success') {
        setPedido((prev) =>
          prev
            ? {
                ...prev,
                direccion_entrega: res.data.data.direccion_entrega,
                lat_entrega: res.data.data.lat_entrega,
                lng_entrega: res.data.data.lng_entrega,
              }
            : prev
        );
        setMostrarCambiarDir(false);
      }
    } catch {
      setModalAlerta('Error al cambiar la dirección de entrega');
    } finally {
      setCambiandoDir(false);
    }
  };

  const productos = React.useMemo(() => {
    if (!pedido?.productos) return [];
    if (Array.isArray(pedido.productos)) return pedido.productos;
    try {
      return JSON.parse(pedido.productos);
    } catch {
      return [];
    }
  }, [pedido?.productos]);

  const total = productos.reduce((s, p) => {
    const precio = p.precio || 0;
    const cant = p.cantidad || 1;
    const base = p.pesable ? (precio * cant) / 1000 : precio * cant;
    return s + base + etiquetaTotal(p);
  }, 0) + (pedido?.costo_envio || 0);

  const estadoIdxActual = pedido ? ESTADO_IDX.indexOf(pedido.estado) : 0;
  const esTerminado =
    pedido &&
    (pedido.estado === 'entregado' ||
      pedido.estado === 'rechazado' ||
      pedido.estado === 'cancelado' ||
      pedido.estado === 'credito_pagado');

  if (cargando) return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px', gap: '16px' }}>
      <div style={{ width: 36, height: 36, borderRadius: '50%', border: '3px solid var(--color-border)', borderTop: '3px solid var(--color-secondary)', animation: 'spin 0.8s linear infinite' }} />
      <div style={{ fontSize: '15px', color: 'var(--color-text-secondary)' }}>Cargando pedido...</div>
    </div>
  );
  if (errorCarga) return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px', gap: '12px' }}>
      <FiXCircle size={48} color="var(--color-danger)" style={{ marginBottom: '8px' }} />
      <div style={{ fontSize: '16px', fontWeight: 600, color: 'var(--color-danger)', textAlign: 'center' }}>{errorCarga}</div>
      <button onClick={() => { setErrorCarga(null); setCargando(true); cargarPedido(); }} style={{ padding: '10px 20px', borderRadius: '10px', border: 'none', backgroundColor: 'var(--color-secondary)', color: '#fff', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', marginTop: '8px' }}>Reintentar</button>
    </div>
  );
  if (!pedido) return <div style={S.loading}>Pedido no encontrado</div>;

  return (
    <div
      style={{
        ...S.wrapper,
        minHeight: inline ? '100%' : undefined,
        height: inline ? 'auto' : S.wrapper.height,
        maxWidth: isDesktop ? '100%' : inline ? '100%' : S.wrapper.maxWidth,
        backgroundColor: inline ? 'transparent' : S.wrapper.backgroundColor,
      }}
    >
      {!inline && (
        <header style={S.header}>
          <button onClick={() => (onBack ? onBack() : navigate(-1))} style={S.backBtn}>
            <FiArrowLeft size={20} />
          </button>
          <div style={{ flex: 1, textAlign: 'center' }}>
            <div style={{ fontSize: '18px', fontWeight: 700, letterSpacing: '-0.3px' }}>
              Tu pedido
            </div>
          </div>
          <span
            style={{
              backgroundColor: ESTADO_COLOR[pedido.estado] || 'var(--color-text-secondary)',
              color: 'var(--color-surface)',
              padding: '4px 12px',
              borderRadius: '12px',
              fontSize: '11px',
              fontWeight: 600,
              whiteSpace: 'nowrap',
            }}
          >
            {ESTADO_LABEL[pedido.estado] || pedido.estado}
          </span>
        </header>
      )}

      <SelectorPedidos
        pedidos={pedidosActivos}
        seleccionadoId={pedidoSeleccionadoId || pedidoId}
        onSeleccionar={onSeleccionarPedido}
      />

      <main
        style={{
          ...S.content,
          ...(inline ? { overflowY: 'visible' } : {}),
          padding: isDesktop ? '24px 32px' : S.content.padding,
          maxWidth: isDesktop ? '960px' : undefined,
          margin: isDesktop ? '0 auto' : undefined,
          width: isDesktop ? '100%' : undefined,
        }}
      >
        <div
          style={{
            ...S.timeline,
            maxWidth: isDesktop ? '800px' : undefined,
            margin: isDesktop ? '0 auto' : undefined,
          }}
        >
          {(() => {
            const FASES = pedido?.condicion_venta === 'credito' ? FASES_CREDITO : FASES_CONTADO;
            return FASES.map((f, i) => {
            const { Icon } = f;
            const fIdx = ESTADO_IDX.indexOf(f.key);
            const done =
              estadoIdxActual > fIdx ||
              (f.key === 'pagado' && estadoIdxActual >= ESTADO_IDX.indexOf('pagado'));
            const active =
              f.key === 'pagado'
                ? estadoIdxActual === ESTADO_IDX.indexOf('pagado')
                : ESTADO_IDX[estadoIdxActual] === f.key ||
                  (estadoIdxActual === ESTADO_IDX.indexOf('esperando_pago') &&
                    f.key === 'aprobado') ||
                  (estadoIdxActual === ESTADO_IDX.indexOf('pago_en_verificacion') &&
                    f.key === 'aprobado') ||
                  (estadoIdxActual === ESTADO_IDX.indexOf('armado_cocina') &&
                    f.key === 'armado');
            return (
              <div
                key={f.key}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <div
                  style={{
                    width: '30px',
                    height: '30px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: done
                      ? 'var(--color-success)'
                      : active
                        ? 'var(--color-secondary)'
                        : 'var(--color-border)',
                    color: done || active ? 'var(--color-surface)' : 'var(--color-text-muted)',
                  }}
                >
                  {done ? (
                    <FiCheckCircle size={14} />
                  ) : active ? (
                    <FiClock size={14} />
                  ) : (
                    <Icon size={14} />
                  )}
                </div>
                <span
                  style={{
                    fontSize: '9px',
                    color: active ? 'var(--color-primary)' : 'var(--color-text-muted)',
                    fontWeight: active ? 600 : 400,
                    marginTop: '4px',
                  }}
                >
                  {f.label}
                </span>
              </div>
            );
          })
          ;})()}
        </div>

        {pedido.confirmacion_pendiente && (
          <div
            style={{
              margin: '12px 0',
              padding: '16px',
              backgroundColor: '#fff8e1',
              borderRadius: '12px',
              border: '2px solid #ff9800',
              boxShadow: '0 2px 12px rgba(255,152,0,0.15)',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px',
                marginBottom: '12px',
              }}
            >
              <div
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  backgroundColor: '#fff3e0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <FiEdit3 size={18} color="#e65100" />
              </div>
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    fontSize: '15px',
                    fontWeight: 700,
                    color: '#e65100',
                    marginBottom: '4px',
                  }}
                >
                  Cambios pendientes de confirmación
                </div>
                <div
                  style={{
                    fontSize: '12px',
                    color: '#bf360c',
                    lineHeight: 1.4,
                  }}
                >
                  El asesor realizó cambios en tu pedido. Revisa los productos y confirma si estás de acuerdo.
                </div>
              </div>
            </div>

            {pedido.notas_asesor && (
              <div
                style={{
                  fontSize: '12px',
                  color: '#5d4037',
                  backgroundColor: '#fff',
                  padding: '10px 12px',
                  borderRadius: '8px',
                  marginBottom: '12px',
                  whiteSpace: 'pre-wrap',
                  border: '1px solid #ffe0b2',
                }}
              >
                <div
                  style={{
                    fontSize: '10px',
                    fontWeight: 700,
                    color: '#e65100',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: '6px',
                  }}
                >
                  Resumen de cambios
                </div>
                {pedido.notas_asesor}
              </div>
            )}

            <div style={{ marginBottom: '12px' }}>
              <div style={{ fontSize: '11px', fontWeight: 700, color: '#e65100', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>
                Productos actualizados
              </div>
              {productos.map((prod, i) => {
                const cant = prod.cantidad || 1;
                const precio = parseFloat(prod.precio || 0);
                const pesable = prod.pesable || false;
                const precioBase = pesable ? (precio * cant) / 1000 : precio * cant;
                const etiqExtra = (prod.etiquetas_extra || 0) || (prod.etiquetas || []).reduce((t, e) => t + (e.precio || 0), 0);
                const subtotal = precioBase + etiqExtra;
                return (
                  <div key={i} style={{
                    display: 'flex', alignItems: 'center', gap: '8px',
                    padding: '8px 12px', backgroundColor: '#fff', borderRadius: '8px',
                    marginBottom: '4px', border: '1px solid #ffe0b2', fontSize: '12px'
                  }}>
                    <div style={{ flex: 1 }}>
                      <span style={{ fontWeight: 600 }}>{prod.nombre || prod.codigo}</span>
                      <span style={{ color: '#757575', marginLeft: '8px' }}>
                        {pesable ? `${(cant / 1000).toFixed(2)} kg` : `${cant} und`} × ${fmtMonto(precio)}
                      </span>
                      {etiqExtra > 0 && (
                        <span style={{ color: '#ff6d00', marginLeft: '4px', fontSize: '11px' }}>
                          + ${fmtMonto(etiqExtra)} aditivos
                        </span>
                      )}
                    </div>
                    <span style={{ fontWeight: 700, color: '#1a73e8' }}>
                      ${fmtMonto(subtotal)}
                    </span>
                  </div>
                );
              })}
            </div>

            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={async () => {
                  try {
                    await axios.put(`${API_URL}/pedidos/${pedido.id}/cliente-rechazo`, {
                      schema_name: schema_name,
                    });
                    cargarPedido();
                  } catch (e) {
                    console.error('Error rechazando:', e);
                  }
                }}
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: '#fff',
                  color: '#d32f2f',
                  border: '2px solid #d32f2f',
                  borderRadius: '10px',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                }}
              >
                <FiXCircle size={16} /> Rechazar cambios
              </button>
              <button
                onClick={async () => {
                  try {
                    await axios.put(`${API_URL}/pedidos/${pedido.id}/cliente-confirmo`, {
                      schema_name: schema_name,
                    });
                    cargarPedido();
                  } catch (e) {
                    console.error('Error confirmando:', e);
                  }
                }}
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: 'var(--color-success)',
                  color: 'var(--color-surface)',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  boxShadow: '0 2px 6px rgba(52,168,83,0.3)',
                }}
              >
                <FiCheckCircle size={16} /> Confirmar cambios
              </button>
            </div>
          </div>
        )}

        {pedido.monto_aprobado && (
          <div style={{ textAlign: 'center', margin: '16px 0' }}>
            <div style={{ fontSize: '11px', color: 'var(--color-text-secondary)' }}>
              Monto a pagar
            </div>
            <div style={{ fontSize: '28px', fontWeight: 800, color: 'var(--color-secondary)' }}>
              ${fmtMonto(pedido.monto_aprobado)}
            </div>
          </div>
        )}

        {pedido.estado === 'rechazado' && (
          <div
            style={{
              backgroundColor: '#FFF0EF',
              padding: '12px',
              borderRadius: '12px',
              textAlign: 'center',
              color: 'var(--color-danger)',
              fontWeight: 600,
              fontSize: '14px',
              marginBottom: '12px',
            }}
          >
            <FiXCircle size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
            Este pedido fue rechazado
          </div>
        )}

        {pedido.estado === 'esperando_pago' &&
          (() => {
            // ── Flujo de pagos divididos (nueva implementacion) ──
            const _pagosPedido = (pedido.pagos || []).slice().sort((a, b) => (a.id || 0) - (b.id || 0));
            const _montoBase = parseFloat(pedido.monto_aprobado || pedido.total || 0);
            const _pagado = _pagosPedido
              .filter((p) => p.estado !== 'rechazado')
              .reduce((s, p) => s + (parseFloat(p.monto) || 0), 0);
            const _saldo = Math.max(0, _montoBase - _pagado);
            const _estadoPago = {
              pendiente: { label: 'Pendiente', color: '#FF9500' },
              aprobado: { label: 'Aprobado', color: 'var(--color-success)' },
              rechazado: { label: 'Rechazado', color: 'var(--color-danger)' },
            };
            const _usarSplit = true;
            if (_usarSplit) {
    const requiereComp = pagoMetodoSel?.requiere_comprobante !== false;
              const datosMostrar = pagoMetodoSel?.datos_negocio?.datos_mostrar;
              const simb = pagoMetodoSel?.simbolo || '$';
              const tasa = pagoMetodoSel?.tasa || 1;
              const pctCubierto = _montoBase > 0 ? Math.min(100, (_pagado / _montoBase) * 100) : 0;
              const pasoLabel = ['Método', 'Monto', 'Comprobante'];
              return (
                <div style={S.card}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <h4 style={{ fontSize: '15px', fontWeight: 800, color: 'var(--color-primary)', margin: 0 }}>
                      {mostrarAgregarPago ? 'Agregar pago' : 'Pagos del pedido'}
                    </h4>
                    {_saldo > 0 && (
                      <span style={{ fontSize: '13px', fontWeight: 800, color: 'var(--color-danger)' }}>
                        Faltan ${fmtMonto(_saldo)}
                      </span>
                    )}
                  </div>

                  <div style={{ height: 8, background: 'var(--color-bg-hover)', borderRadius: 4, overflow: 'hidden', marginBottom: '14px' }}>
                    <div style={{ height: '100%', width: `${pctCubierto}%`, background: 'var(--color-success)', borderRadius: 4, transition: 'width 0.3s ease' }} />
                  </div>

                  {!mostrarAgregarPago ? (
                    <>
                      {_pagosPedido.length === 0 && (
                        <div style={{ fontSize: '13px', color: 'var(--color-text-secondary)', marginBottom: '12px' }}>
                          Aún no has registrado ningún pago. Toca "Agregar pago" para empezar.
                        </div>
                      )}
                      {_pagosPedido.map((p) => {
                        const st = _estadoPago[p.estado] || _estadoPago.pendiente;
                        return (
                          <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', border: '1px solid var(--color-border)', borderRadius: '12px', padding: '12px', marginBottom: '10px', background: p.estado === 'rechazado' ? '#FFF0EF' : 'var(--color-surface)' }}>
                            <span style={{ width: 38, height: 38, borderRadius: 11, background: 'var(--color-bg-hover)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-secondary)', flexShrink: 0 }}>
                              <FiCreditCard size={18} />
                            </span>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontWeight: 700, fontSize: '13px', color: 'var(--color-primary)' }}>{p.metodo_nombre || 'Método de pago'}</div>
                              <div style={{ fontSize: '13px', fontWeight: 700 }}>${fmtMonto(p.monto)}</div>
                              {p.estado === 'rechazado' && p.notas && (
                                <div style={{ fontSize: '11px', color: 'var(--color-danger)', marginTop: '2px' }}>{p.notas}</div>
                              )}
                            </div>
                            {p.comprobante_url && (
                              <img src={getImagenUrl(p.comprobante_url)} alt="Comprobante" style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 8, flexShrink: 0, border: '1px solid var(--color-border)' }} onError={(e) => (e.currentTarget.style.display = 'none')} />
                            )}
                            <span style={{ fontSize: '10px', fontWeight: 700, padding: '3px 8px', borderRadius: 8, background: st.color + '22', color: st.color, flexShrink: 0 }}>{st.label}</span>
                          </div>
                        );
                      })}
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0 4px', fontSize: '13px', borderTop: '1px solid var(--color-border)', marginTop: '4px' }}>
                        <span style={{ color: 'var(--color-text-secondary)' }}>Total a cubrir</span>
                        <span style={{ fontWeight: 700 }}>${fmtMonto(_montoBase)}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0 12px', fontSize: '13px' }}>
                        <span style={{ color: _saldo > 0 ? 'var(--color-danger)' : 'var(--color-success)' }}>{_saldo > 0 ? 'Saldo pendiente' : 'Total cubierto'}</span>
                        <span style={{ fontWeight: 800, color: _saldo > 0 ? 'var(--color-danger)' : 'var(--color-success)' }}>${fmtMonto(_saldo)}</span>
                      </div>
                      <button
                        onClick={() => { setMostrarAgregarPago(true); setPasoPago(1); }}
                        style={{ width: '100%', padding: '14px', borderRadius: '14px', border: 'none', background: 'var(--color-secondary)', color: 'var(--color-surface)', fontWeight: 700, fontSize: '14px', cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 4px 16px rgba(41,82,204,0.25)' }}
                      >
                        + Agregar pago
                      </button>
                    </>
                  ) : (
                    <div>
                      <div style={{ display: 'flex', gap: 6, marginBottom: '18px' }}>
                        {pasoLabel.map((l, i) => (
                          <div key={l} style={{ flex: 1 }}>
                            <div style={{ height: 4, borderRadius: 2, background: pasoPago >= i + 1 ? 'var(--color-secondary)' : 'var(--color-bg-hover)', transition: 'background 0.2s' }} />
                            <div style={{ fontSize: 10, fontWeight: pasoPago === i + 1 ? 700 : 500, color: pasoPago === i + 1 ? 'var(--color-primary)' : 'var(--color-text-muted)', marginTop: 4, textAlign: 'center' }}>{i + 1}. {l}</div>
                          </div>
                        ))}
                      </div>

                      {pasoPago === 1 && (
                        <>
                          <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--color-text-secondary)', marginBottom: '10px' }}>¿Cómo quieres pagar?</div>
                          {metodosPago.map((m) => {
                            const tasaM = m.tasa || 1;
                            const simbM = m.simbolo || '$';
                            const dnM = m.datos_negocio || {};
                            const porc = parseFloat(dnM.porcentaje_comision || 0) || 0;
                            const recargo = parseFloat(dnM.recargo_extra || 0) || 0;
                            // Monto restante a cancelar (saldo en USD) convertido a la moneda del método,
                            // sumando comisión/recargo si aplica.
                            const saldoLocal = _saldo * tasaM;
                            const montoMostrar = m.tiene_comision
                              ? saldoLocal * (1 + porc / 100) + recargo * tasaM
                              : saldoLocal;
                            return (
                              <button
                                key={m.id}
                                onClick={() => { setPagoMetodoId(m.id); setPagoMetodoSel(m); setPasoPago(2); }}
                                style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', padding: '14px 16px', borderRadius: '14px', marginBottom: '8px', border: '1.5px solid var(--color-border)', background: 'var(--color-surface)', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left' }}
                              >
                                <span style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                  <span style={{ width: 38, height: 38, borderRadius: 11, background: 'var(--color-bg-hover)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-secondary)', flexShrink: 0 }}>
                                    <FiCreditCard size={18} />
                                  </span>
                                  <span style={{ fontSize: '14px', fontWeight: 700, color: 'var(--color-primary)' }}>{m.nombre}</span>
                                </span>
                                <span style={{ fontSize: '14px', fontWeight: 800, color: 'var(--color-secondary)' }}>{simbM}{fmtMonto(montoMostrar)}</span>
                              </button>
                            );
                          })}
                          <button onClick={() => setMostrarAgregarPago(false)} style={{ width: '100%', padding: '10px', background: 'none', border: 'none', color: 'var(--color-text-muted)', fontSize: '12px', cursor: 'pointer', marginTop: '4px', fontFamily: 'inherit' }}>Cancelar</button>
                        </>
                      )}

                      {pasoPago === 2 && (
                        <>
                          <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--color-text-secondary)', marginBottom: '12px' }}>
                            ¿Cuánto pagas con {pagoMetodoSel?.nombre}?
                          </div>
                          <button
                            onClick={() => { setPagoMonto(String((_saldo * tasa).toFixed(2))); setPagoMontoCustom(false); }}
                            style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px', borderRadius: '14px', marginBottom: '8px', border: !pagoMontoCustom ? '2px solid var(--color-secondary)' : '1.5px solid var(--color-border)', background: !pagoMontoCustom ? 'var(--color-bg-hover)' : 'var(--color-surface)', cursor: 'pointer', fontFamily: 'inherit' }}
                          >
                            <span style={{ fontSize: '15px', fontWeight: 800, color: 'var(--color-primary)' }}>Total completo</span>
                            <span style={{ fontSize: '14px', fontWeight: 800, color: 'var(--color-secondary)' }}>{simb}{fmtMonto(_saldo * tasa)}</span>
                          </button>
                          <button
                            onClick={() => { setPagoMonto(''); setPagoMontoCustom(true); }}
                            style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px', borderRadius: '14px', border: pagoMontoCustom ? '2px solid var(--color-secondary)' : '1.5px solid var(--color-border)', background: pagoMontoCustom ? 'var(--color-bg-hover)' : 'var(--color-surface)', cursor: 'pointer', fontFamily: 'inherit' }}
                          >
                            <span style={{ fontSize: '15px', fontWeight: 800, color: 'var(--color-primary)' }}>Otra cantidad</span>
                            <span style={{ fontSize: '13px', color: 'var(--color-text-secondary)' }}>Elige un monto</span>
                          </button>
                          {pagoMontoCustom && (
                            <div style={{ marginTop: '10px' }}>
                              <input
                                type="number"
                                inputMode="decimal"
                                min="0"
                                step="0.01"
                                placeholder={`0.00 ${simb}`}
                                value={pagoMonto}
                                onChange={(e) => setPagoMonto(e.target.value)}
                                autoFocus
                                style={{ width: '100%', padding: '16px', borderRadius: '14px', border: '2px solid var(--color-secondary)', fontSize: '24px', fontWeight: 800, fontFamily: 'inherit', textAlign: 'center', outline: 'none', color: 'var(--color-primary)', background: 'var(--color-surface)' }}
                              />
                              <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: 4, textAlign: 'center' }}>Máximo: {simb}{fmtMonto(_saldo * tasa)}</div>
                            </div>
                          )}
                          <div style={{ display: 'flex', gap: 8, marginTop: '18px' }}>
                            <button onClick={() => setPasoPago(1)} style={{ flex: 1, padding: '13px', borderRadius: '12px', border: '1px solid var(--color-border)', background: 'var(--color-surface)', color: 'var(--color-text-secondary)', fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>Atrás</button>
                            <button
                              onClick={() => {
                                if (!pagoMonto || parseFloat(pagoMonto) <= 0) { setModalAlerta('Selecciona un monto'); return; }
                                if (parseFloat(pagoMonto) > (_saldo * tasa) + 0.01) { setModalAlerta('El monto no puede superar el saldo pendiente'); return; }
                                setPasoPago(3);
                              }}
                              style={{ flex: 1, padding: '13px', borderRadius: '12px', border: 'none', background: 'var(--color-secondary)', color: 'var(--color-surface)', fontSize: '13px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}
                            >
                              Continuar
                            </button>
                          </div>
                        </>
                      )}

                      {pasoPago === 3 && (
                        <>
                          {requiereComp ? (
                            <>
                              {datosMostrar && Array.isArray(datosMostrar) && datosMostrar.length > 0 && (
                                <div style={{ background: 'var(--color-bg-hover)', borderRadius: '12px', padding: '12px', marginBottom: '12px' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                    <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--color-secondary)' }}>Datos para realizar el pago</div>
                                    <button onClick={copiarDatosPago} style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '6px 12px', borderRadius: '8px', border: 'none', background: 'var(--color-secondary)', color: 'var(--color-surface)', fontSize: '11px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                                      {copiado ? <FiCheckCircle size={13} /> : <FiCopy size={13} />}
                                      {copiado ? 'Copiado' : 'Copiar datos'}
                                    </button>
                                  </div>
                                  <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--color-primary)', marginBottom: '6px' }}>Monto: {simb}{fmtMonto(pagoMonto)}</div>
                                  {datosMostrar.map((d, i) => (
                                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid var(--color-border)', fontSize: '13px' }}>
                                      <span style={{ color: 'var(--color-text-secondary)' }}>{d.etiqueta}</span>
                                      <span style={{ fontWeight: 600 }}>{d.valor}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                              <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--color-text-secondary)', marginBottom: '10px' }}>Sube tu comprobante de pago</div>
                              <input
                                type="file"
                                accept="image/*,.pdf"
                                id="pagoArchivo"
                                style={{ display: 'none' }}
                                onChange={(e) => {
                                  const f = e.target.files[0];
                                  if (f) {
                                    setPagoArchivo(f);
                                    if (f.type.startsWith('image/')) {
                                      const r = new FileReader();
                                      r.onload = (ev) => setPagoPreview(ev.target.result);
                                      r.readAsDataURL(f);
                                    } else {
                                      setPagoPreview(null);
                                    }
                                  }
                                }}
                              />
                              {pagoPreview ? (
                                <img src={pagoPreview} alt="Vista previa" style={{ width: '100%', maxHeight: '200px', objectFit: 'contain', borderRadius: '12px', marginBottom: '10px', border: '1px solid var(--color-border)' }} />
                              ) : pagoArchivo ? (
                                <div style={{ padding: '14px', background: 'var(--color-bg)', borderRadius: '12px', fontSize: '13px', color: 'var(--color-success)', marginBottom: '10px', textAlign: 'center' }}>Archivo: {pagoArchivo.name}</div>
                              ) : (
                                <label
                                  htmlFor="pagoArchivo"
                                  style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '28px', borderRadius: '14px', border: '2px dashed var(--color-secondary)', background: 'var(--color-bg-hover)', color: 'var(--color-secondary)', cursor: 'pointer', textAlign: 'center' }}
                                >
                                  <FiUpload size={26} />
                                  <span style={{ fontSize: '14px', fontWeight: 700 }}>Subir comprobante</span>
                                  <span style={{ fontSize: '11px', color: 'var(--color-text-muted)' }}>Imagen o PDF</span>
                                </label>
                              )}
                            </>
                          ) : (
                            <div style={{ textAlign: 'center', padding: '24px 0' }}>
                              <FiCheckCircle size={32} color="var(--color-success)" style={{ marginBottom: '8px' }} />
                              <div style={{ fontSize: '13px', color: 'var(--color-text-secondary)' }}>Este método no requiere comprobante.</div>
                            </div>
                          )}
                          <div style={{ display: 'flex', gap: 8, marginTop: '18px' }}>
                            <button onClick={() => setPasoPago(2)} style={{ flex: 1, padding: '13px', borderRadius: '12px', border: '1px solid var(--color-border)', background: 'var(--color-surface)', color: 'var(--color-text-secondary)', fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>Atrás</button>
                            <button onClick={submitPago} disabled={agregandoPago} style={{ flex: 1.5, padding: '13px', borderRadius: '12px', border: 'none', background: 'var(--color-secondary)', color: 'var(--color-surface)', fontSize: '13px', fontWeight: 700, cursor: agregandoPago ? 'default' : 'pointer', fontFamily: 'inherit', boxShadow: '0 4px 16px rgba(41,82,204,0.25)' }}>
                              {agregandoPago ? 'Guardando...' : 'Confirmar pago'}
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            }
            const pagoRechazado = !!(
              pedido.comprobante_url &&
              pedido.notas_asesor &&
              pedido.notas_asesor.includes('rechazado')
            );

            if (pagoRechazado && !reintentandoPago) {
              return (
                <div
                  style={{
                    ...S.card,
                    backgroundColor: '#FFF0EF',
                    border: '1px solid var(--color-danger)',
                  }}
                >
                  <div style={{ textAlign: 'center' }}>
                    <FiXCircle
                      size={36}
                      color="var(--color-danger)"
                      style={{ marginBottom: '8px' }}
                    />
                    <div
                      style={{
                        fontSize: '14px',
                        fontWeight: 700,
                        color: 'var(--color-danger)',
                        marginBottom: '4px',
                      }}
                    >
                      Pago rechazado
                    </div>
                    <div
                      style={{
                        fontSize: '12px',
                        color: 'var(--color-text-secondary)',
                        marginBottom: '12px',
                      }}
                    >
                      Verifica el comprobante enviado y los datos solicitados. Si tienes dudas,
                      consulta con un asesor en el chat.
                    </div>
                    <button
                      onClick={() => {
                        setReintentandoPago(true);
                        setCompFile(null);
                        setCompPreview(null);
                      }}
                      style={{
                        padding: '10px 20px',
                        backgroundColor: 'var(--color-danger)',
                        color: 'var(--color-surface)',
                        border: 'none',
                        borderRadius: '10px',
                        fontSize: '13px',
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      Reenviar comprobante
                    </button>
                  </div>
                </div>
              );
            }

            let dp = pedido.datos_pago;
            if (typeof dp === 'string')
              try {
                dp = JSON.parse(dp);
              } catch {
                dp = null;
              }
            const yaSelecciono = dp && dp.metodo_pago_id && !cambiandoMetodo;

            if (!yaSelecciono) {
              return (
                <div style={S.card}>
                  <h4
                    style={{
                      fontSize: '14px',
                      fontWeight: 700,
                      color: 'var(--color-primary)',
                      margin: '0 0 12px',
                    }}
                  >
                    Selecciona método de pago
                  </h4>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                    {metodosPago.map((m) => (
                      <button
                        key={m.id}
                        onClick={() => {
                          setMetodoPago(m.id);
                          setMetodoSeleccionado(m);
                        }}
                        style={{
                          padding: '12px 8px',
                          borderRadius: '14px',
                          fontSize: '13px',
                          fontWeight: 600,
                          cursor: 'pointer',
                          border:
                            metodoPago === m.id
                              ? '2px solid var(--color-secondary)'
                              : '1px solid var(--color-border)',
                          backgroundColor:
                            metodoPago === m.id ? 'var(--color-bg-hover)' : 'var(--color-surface)',
                          color:
                            metodoPago === m.id ? 'var(--color-secondary)' : 'var(--color-text)',
                        }}
                      >
                        <FiDollarSign size={14} style={{ marginRight: '6px' }} /> {m.nombre}
                      </button>
                    ))}
                  </div>
                  {metodoSeleccionado && (
                    <div style={{ marginTop: '12px', textAlign: 'center' }}>
                      <button
                        onClick={async () => {
                          try {
                            await axios.put(`${API_URL}/pedidos/${pedido.id}/seleccionar-pago`, {
                              schema_name: schema_name,
                              metodo_pago_id: metodoPago,
                              datos_cliente: {},
                            });
                            setCambiandoMetodo(false);
                            cargarPedido();
    } catch (e) {
      console.error('Error:', e);
      agregarNotificacion({ tipo: 'error', mensaje: 'Error al cambiar metodo de pago. Intenta de nuevo.', duracion: 5000 });
      setCambiandoMetodo(false);
                          }
                        }}
                        style={{
                          padding: '12px 28px',
                          backgroundColor: 'var(--color-secondary)',
                          color: 'var(--color-surface)',
                          border: 'none',
                          borderRadius: '24px',
                          fontSize: '14px',
                          fontWeight: 700,
                          cursor: 'pointer',
                          boxShadow: '0 4px 16px rgba(41,82,204,0.25)',
                        }}
                      >
                        Continuar con {metodoSeleccionado.nombre}
                      </button>
                    </div>
                  )}
                </div>
              );
            }

            const metodoInfo = metodosPago.find((m) => m.id === dp.metodo_pago_id);
            if (!metodoInfo) return null;
            const dn = metodoInfo.datos_negocio || {};
            const esPaypal = metodoInfo.tiene_comision;
            const esPagoMovil = metodoInfo.codigo === 'pago_movil';
            const pmCampos = dn.pago_movil_campos || (esPagoMovil ? ['CI', 'Tlf', 'Banco'] : []);
            const requiereComp = dn.requiere_comprobante !== false;

            return (
              <div style={S.card}>
                <h4
                  style={{
                    fontSize: '14px',
                    fontWeight: 700,
                    color: 'var(--color-primary)',
                    margin: '0 0 12px',
                  }}
                >
                  Pago · {metodoInfo.nombre}
                </h4>

                {esPaypal && (
                  <div
                    style={{
                      padding: '14px',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '12px',
                      marginBottom: '10px',
                      border: '1px solid var(--color-border)',
                    }}
                  >
                    <div
                      style={{
                        fontSize: '13px',
                        fontWeight: 700,
                        color: 'var(--color-primary)',
                        marginBottom: '10px',
                      }}
                    >
                      Desglose de pago
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        padding: '3px 0',
                        fontSize: '13px',
                      }}
                    >
                      <span>Monto del pedido</span>
                      <span style={{ fontWeight: 600 }}>${fmtMonto(metodoInfo.monto_base)}</span>
                    </div>
                    {(pedido.costo_envio || 0) > 0 && (
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          padding: '3px 0',
                          fontSize: '13px',
                          color: 'var(--color-text-secondary)',
                        }}
                      >
                        <span>  Incluye costo de envio</span>
                        <span style={{ fontWeight: 600 }}>${fmtMonto(pedido.costo_envio)}</span>
                      </div>
                    )}
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        padding: '3px 0',
                        fontSize: '13px',
                        color: 'var(--color-danger)',
                      }}
                    >
                      <span>Comision ({dn.porcentaje_comision || 0}%)</span>
                      <span style={{ fontWeight: 600 }}>
                        ${fmtMonto(metodoInfo.monto_comision)}
                      </span>
                    </div>
                    {(dn.recargo_extra || 0) > 0 && (
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          padding: '3px 0',
                          fontSize: '13px',
                          color: 'var(--color-danger)',
                        }}
                      >
                        <span>Recargo plataforma</span>
                        <span style={{ fontWeight: 600 }}>
                          ${fmtMonto(metodoInfo.monto_recargo)}
                        </span>
                      </div>
                    )}
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        padding: '10px 0 0',
                        marginTop: '10px',
                        borderTop: '2px solid var(--color-secondary)',
                        fontWeight: 800,
                        fontSize: '16px',
                      }}
                    >
                      <span>Total a cancelar</span>
                      <span style={{ color: 'var(--color-secondary)' }}>
                        ${fmtMonto(metodoInfo.monto_total)}
                      </span>
                    </div>
                    {dn.correo && (
                      <div
                        style={{
                          marginTop: '10px',
                          padding: '10px',
                          backgroundColor: 'var(--color-bg-hover)',
                          borderRadius: '8px',
                          fontSize: '13px',
                          textAlign: 'center',
                        }}
                      >
                        Enviar pago a: <strong>{dn.correo}</strong>
                      </div>
                    )}
                  </div>
                )}

                {!esPaypal && Object.keys(dn).length > 0 && (
                  <div
                    style={{
                      padding: '12px',
                      backgroundColor: 'var(--color-bg-hover)',
                      borderRadius: '10px',
                      marginBottom: '10px',
                      border: '1px solid var(--color-secondary)',
                    }}
                  >
                    <div
                      style={{
                        fontSize: '12px',
                        fontWeight: 600,
                        color: 'var(--color-secondary)',
                        marginBottom: '8px',
                      }}
                    >
                      Datos para realizar el pago
                    </div>
                    {(dn.datos_mostrar && Array.isArray(dn.datos_mostrar)
                      ? dn.datos_mostrar
                      : []
                    ).map((d, i) => (
                      <div
                        key={i}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          padding: '6px 0',
                          borderBottom: '1px solid #c6dafc',
                          fontSize: '13px',
                        }}
                      >
                        <span style={{ color: 'var(--color-text-secondary)' }}>{d.etiqueta}</span>
                        <span style={{ fontWeight: 600, color: 'var(--color-text)' }}>
                          {d.valor}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {!esPaypal && (
                  <div
                    style={{
                      textAlign: 'center',
                      padding: '10px 0',
                      fontWeight: 700,
                      fontSize: '15px',
                      color: 'var(--color-primary)',
                    }}
                  >
                    Monto a cancelar:{' '}
                    <span style={{ color: 'var(--color-secondary)' }}>
                      {metodoInfo.simbolo}
                      {fmtMonto(metodoInfo.monto_mostrar)}
                    </span>
                  </div>
                )}

                {esPagoMovil && pmCampos.length > 0 && (
                  <div style={{ marginTop: '8px' }}>
                    <div
                      style={{
                        fontSize: '12px',
                        fontWeight: 600,
                        color: 'var(--color-danger)',
                        marginBottom: '6px',
                      }}
                    >
                      Datos obligatorios para verificar el pago:
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {pmCampos.includes('CI') && (
                        <input
                          type="text"
                          placeholder="Cedula/RIF *"
                          value={datosCliente['Cedula/RIF'] || ''}
                          onChange={(e) =>
                            setDatosCliente((prev) => ({ ...prev, 'Cedula/RIF': e.target.value }))
                          }
                          style={{
                            padding: '10px 14px',
                            borderRadius: '10px',
                            border: '1px solid var(--color-border)',
                            fontSize: '13px',
                            fontFamily: 'inherit',
                            outline: 'none',
                          }}
                        />
                      )}
                      {pmCampos.includes('Tlf') && (
                        <input
                          type="text"
                          placeholder="Telefono *"
                          value={datosCliente['Telefono'] || ''}
                          onChange={(e) =>
                            setDatosCliente((prev) => ({ ...prev, Telefono: e.target.value }))
                          }
                          style={{
                            padding: '10px 14px',
                            borderRadius: '10px',
                            border: '1px solid var(--color-border)',
                            fontSize: '13px',
                            fontFamily: 'inherit',
                            outline: 'none',
                          }}
                        />
                      )}
                      {pmCampos.includes('Banco') && (
                        <input
                          type="text"
                          placeholder="Banco *"
                          value={datosCliente['Banco'] || ''}
                          onChange={(e) =>
                            setDatosCliente((prev) => ({ ...prev, Banco: e.target.value }))
                          }
                          style={{
                            padding: '10px 14px',
                            borderRadius: '10px',
                            border: '1px solid var(--color-border)',
                            fontSize: '13px',
                            fontFamily: 'inherit',
                            outline: 'none',
                          }}
                        />
                      )}
                    </div>
                  </div>
                )}

                {(dn.datos_cliente || []).length > 0 && (
                  <div style={{ marginTop: '8px' }}>
                    <div
                      style={{
                        fontSize: '12px',
                        fontWeight: 600,
                        color: 'var(--color-danger)',
                        marginBottom: '6px',
                      }}
                    >
                      Datos requeridos:
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {dn.datos_cliente.map((c, i) => (
                        <input
                          key={i}
                          type="text"
                          placeholder={c.nombre + (c.requerido !== false ? ' *' : '')}
                          value={datosCliente[c.nombre] || ''}
                          onChange={(e) =>
                            setDatosCliente((prev) => ({ ...prev, [c.nombre]: e.target.value }))
                          }
                          style={{
                            padding: '10px 14px',
                            borderRadius: '10px',
                            border: '1px solid var(--color-border)',
                            fontSize: '13px',
                            fontFamily: 'inherit',
                            outline: 'none',
                          }}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {requiereComp ? (
                  <div
                    style={{
                      borderTop: '1px solid var(--color-border)',
                      paddingTop: '12px',
                      marginTop: '12px',
                    }}
                  >
                    <div style={{ textAlign: 'center' }}>
                      <input
                        type="file"
                        accept="image/*,.pdf"
                        id="compFile"
                        style={{ display: 'none' }}
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            setCompFile(file);
                            if (file.type.startsWith('image/')) {
                              const reader = new FileReader();
                              reader.onload = (ev) => setCompPreview(ev.target.result);
                              reader.readAsDataURL(file);
                            } else {
                              setCompPreview(null);
                            }
                          }
                        }}
                      />
                      {compPreview && (
                        <div
                          style={{
                            marginBottom: '12px',
                            borderRadius: '12px',
                            overflow: 'hidden',
                            border: '1px solid var(--color-border)',
                          }}
                        >
                          <img
                            src={compPreview}
                            alt="Comprobante"
                            style={{
                              width: '100%',
                              maxHeight: '250px',
                              objectFit: 'contain',
                              display: 'block',
                            }}
                          />
                        </div>
                      )}
                      {compFile && !compPreview && (
                        <div
                          style={{
                            marginBottom: '12px',
                            padding: '12px',
                            backgroundColor: 'var(--color-bg)',
                            borderRadius: '10px',
                            fontSize: '13px',
                            color: 'var(--color-text-secondary)',
                          }}
                        >
                          Archivo: {compFile.name}
                        </div>
                      )}
                      <label
                        htmlFor="compFile"
                        style={{
                          display: 'inline-block',
                          padding: '12px 28px',
                          backgroundColor: subiendoComp
                            ? 'var(--color-text-secondary)'
                            : compFile
                              ? 'var(--color-secondary)'
                              : 'var(--color-success)',
                          color: 'var(--color-surface)',
                          borderRadius: '24px',
                          fontSize: '14px',
                          fontWeight: 600,
                          cursor: subiendoComp ? 'default' : 'pointer',
                        }}
                      >
                        <FiCheckCircle
                          size={16}
                          style={{ marginRight: '8px', verticalAlign: 'middle' }}
                        />
                        {subiendoComp
                          ? 'Subiendo...'
                          : compFile
                            ? 'Enviar comprobante'
                            : 'Subir comprobante de pago'}
                      </label>
                      {compFile && !subiendoComp && (
                        <div style={{ marginTop: '8px' }}>
                          <button
                            onClick={async () => {
                              if (esPagoMovil && pmCampos.length > 0) {
                                const faltantes = [];
                                if (
                                  pmCampos.includes('Tlf') &&
                                  !(datosCliente['Telefono'] || '').trim()
                                )
                                  faltantes.push('Telefono');
                                if (
                                  pmCampos.includes('CI') &&
                                  !(datosCliente['Cedula/RIF'] || '').trim()
                                )
                                  faltantes.push('Cedula/RIF');
                                if (
                                  pmCampos.includes('Banco') &&
                                  !(datosCliente['Banco'] || '').trim()
                                )
                                  faltantes.push('Banco');
                                if (faltantes.length > 0) {
                                  setModalAlerta(`Pago Movil requiere: ${faltantes.join(', ')}`);
                                  return;
                                }
                              }
                              const dcFields = dn.datos_cliente || [];
                              if (dcFields.length > 0) {
                                const faltantes = dcFields
                                  .filter(
                                    (c) =>
                                      c.requerido !== false &&
                                      !(datosCliente[c.nombre] || '').trim()
                                  )
                                  .map((c) => c.nombre);
                                if (faltantes.length > 0) {
                                  setModalAlerta(`Faltan datos: ${faltantes.join(', ')}`);
                                  return;
                                }
                              }
                              setSubiendoComp(true);
                              try {
                                const formData = new FormData();
                                formData.append('archivo', compFile);
                                formData.append('metodo_pago', pedido.metodo_pago || '');
                                formData.append('schema_name', schema_name);
                                if (esPagoMovil) {
                                  formData.append(
                                    'datos_cliente',
                                    JSON.stringify({
                                      Telefono: datosCliente['Telefono'] || '',
                                      'Cedula/RIF': datosCliente['Cedula/RIF'] || '',
                                      Banco: datosCliente['Banco'] || '',
                                    })
                                  );
                                } else if (dcFields.length > 0) {
                                  formData.append('datos_cliente', JSON.stringify(datosCliente));
                                }
                                const res = await axios.post(
                                  `${API_URL}/pedidos/${pedido.id}/comprobante`,
                                  formData
                                );
                                if (res.data.status === 'success') {
                                  setPedido((prev) => ({
                                    ...prev,
                                    estado: 'pago_en_verificacion',
                                    comprobante_url: res.data.data.comprobante_url,
                                  }));
                                  cargarPedido();
                                }
                              } catch (e) {
                                console.error('Error:', e);
                              }
                              setSubiendoComp(false);
                            }}
                            style={{
                              padding: '8px 18px',
                              backgroundColor: 'var(--color-success)',
                              color: 'var(--color-surface)',
                              border: 'none',
                              borderRadius: '8px',
                              fontSize: '13px',
                              fontWeight: 600,
                              cursor: 'pointer',
                            }}
                          >
                            Confirmar pago
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div
                    style={{
                      borderTop: '1px solid var(--color-border)',
                      paddingTop: '12px',
                      marginTop: '12px',
                      textAlign: 'center',
                    }}
                  >
                    <button
                      onClick={async () => {
                        if (esPagoMovil && pmCampos.length > 0) {
                          const faltantes = [];
                          if (pmCampos.includes('Tlf') && !(datosCliente['Telefono'] || '').trim())
                            faltantes.push('Telefono');
                          if (pmCampos.includes('CI') && !(datosCliente['Cedula/RIF'] || '').trim())
                            faltantes.push('Cedula/RIF');
                          if (pmCampos.includes('Banco') && !(datosCliente['Banco'] || '').trim())
                            faltantes.push('Banco');
                          if (faltantes.length > 0) {
                            setModalAlerta(`Pago Movil requiere: ${faltantes.join(', ')}`);
                            return;
                          }
                        }
                        const dcFields = dn.datos_cliente || [];
                        if (dcFields.length > 0) {
                          const faltantes = dcFields
                            .filter(
                              (c) => c.requerido !== false && !(datosCliente[c.nombre] || '').trim()
                            )
                            .map((c) => c.nombre);
                          if (faltantes.length > 0) {
                            setModalAlerta(`Faltan datos: ${faltantes.join(', ')}`);
                            return;
                          }
                        }
                        setSubiendoComp(true);
                        try {
                          const formData = new FormData();
                          formData.append('metodo_pago', pedido.metodo_pago || '');
                          formData.append('schema_name', schema_name);
                          if (esPagoMovil) {
                            formData.append(
                              'datos_cliente',
                              JSON.stringify({
                                Telefono: datosCliente['Telefono'] || '',
                                'Cedula/RIF': datosCliente['Cedula/RIF'] || '',
                                Banco: datosCliente['Banco'] || '',
                              })
                            );
                          } else if (dcFields.length > 0) {
                            formData.append('datos_cliente', JSON.stringify(datosCliente));
                          }
                          const res = await axios.post(
                            `${API_URL}/pedidos/${pedido.id}/confirmar-pago`,
                            formData
                          );
                          if (res.data.status === 'success') {
                            setPedido((prev) => ({ ...prev, estado: 'pago_en_verificacion' }));
                            cargarPedido();
                          }
                        } catch (e) {
                          console.error('Error:', e);
                        }
                        setSubiendoComp(false);
                      }}
                      style={{
                        padding: '12px 32px',
                        backgroundColor: 'var(--color-secondary)',
                        color: 'var(--color-surface)',
                        border: 'none',
                        borderRadius: '24px',
                        fontSize: '14px',
                        fontWeight: 700,
                        cursor: 'pointer',
                        boxShadow: '0 4px 16px rgba(41,82,204,0.25)',
                      }}
                    >
                      Confirmar pago
                    </button>
                  </div>
                )}

                <button
                  onClick={() => {
                    setCambiandoMetodo(true);
                    setMetodoPago('');
                    setMetodoSeleccionado(null);
                    setDatosCliente({});
                  }}
                  style={{
                    marginTop: '10px',
                    padding: '8px',
                    background: 'none',
                    border: 'none',
                    color: 'var(--color-secondary)',
                    cursor: 'pointer',
                    fontSize: '12px',
                    width: '100%',
                  }}
                >
                  Cambiar método de pago
                </button>
              </div>
            );
          })()}

        {pedido.estado === 'pago_en_verificacion' && (
          <div style={{ ...S.card, textAlign: 'center' }}>
            <FiClock size={36} color="#fbbc04" style={{ marginBottom: '8px' }} />
            <p style={{ fontSize: '14px', color: 'var(--color-text-secondary)', margin: 0 }}>
              Verificando pago
            </p>
            <p
              style={{ fontSize: '12px', color: 'var(--color-text-secondary)', margin: '4px 0 0' }}
            >
              El asesor esta revisando tu comprobante
            </p>
            {pedido.metodo_pago && (
              <span
                style={{
                  marginTop: '8px',
                  display: 'inline-block',
                  fontSize: '12px',
                  backgroundColor: 'var(--color-bg-hover)',
                  color: 'var(--color-secondary)',
                  padding: '4px 12px',
                  borderRadius: '10px',
                  fontWeight: 600,
                }}
              >
                {pedido.metodo_pago}
              </span>
            )}
          </div>
        )}

        {(pedido.estado === 'armado' ||
          pedido.estado === 'listo' ||
          pedido.estado === 'despachado' ||
          pedido.estado === 'en_camino' ||
          pedido.estado === 'llegada') && (
          <div style={S.card}>
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              {pedido.estado === 'armado' && <FiPackage size={32} color="#ff6d00" />}
              {pedido.estado === 'listo' && <FiBox size={32} color="var(--color-secondary)" />}
              {pedido.estado === 'despachado' && <FiTruck size={32} color="#00bcd4" />}
              {pedido.estado === 'en_camino' && <FiTruck size={32} color="#00bcd4" />}
              {pedido.estado === 'llegada' && <FiFlag size={32} color="#009688" />}
              <div
                style={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: 'var(--color-primary)',
                  marginTop: '8px',
                }}
              >
                {pedido.estado === 'armado' && 'Tu pedido esta siendo armado'}
                {pedido.estado === 'listo' &&
                  (pedido.tipo_entrega === 'retiro'
                    ? 'Tu pedido esta listo para retirar'
                    : 'Tu pedido esta armado y listo')}
                {pedido.estado === 'despachado' && 'Tu pedido salio del local'}
                {pedido.estado === 'en_camino' && 'Tu pedido va en camino'}
                {pedido.estado === 'llegada' && 'El delivery ha llegado, sal a recibir tu pedido'}
              </div>
              <div
                style={{ fontSize: '12px', color: 'var(--color-text-secondary)', marginTop: '4px' }}
              >
                {pedido.estado === 'listo' &&
                  pedido.tipo_entrega === 'retiro' &&
                  'Ya puedes pasar a retirarlo en la tienda.'}
                {pedido.estado === 'listo' &&
                  pedido.tipo_entrega === 'encomienda' &&
                  'Pronto saldra del local por encomienda.'}
                {pedido.estado === 'listo' &&
                  (!pedido.tipo_entrega || pedido.tipo_entrega === 'delivery') &&
                  'A la espera de que un delivery salga a entregarlo.'}
                {pedido.estado === 'despachado' &&
                  'Tu pedido va por encomienda. Confirma la recepcion cuando lo recibas.'}
                {(pedido.estado === 'en_camino' || pedido.estado === 'llegada') &&
                  'El delivery se comunicara contigo para concretar la entrega.'}
              </div>
              {(pedido.estado === 'llegada' || pedido.estado === 'despachado') && (
                <button
                  onClick={async () => {
                    try {
                      await axios.put(`${API_URL}/pedidos/${pedido.id}/recibido`, {
                        schema_name: schema_name,
                      });
                      setPedido((prev) => (prev ? { ...prev, estado: 'entregado' } : prev));
                      cargarPedido();
                    } catch (e) {
                      console.error('Error:', e);
                    }
                  }}
                  style={{
                    marginTop: '16px',
                    padding: '14px 32px',
                    backgroundColor: 'var(--color-success)',
                    color: 'var(--color-surface)',
                    border: 'none',
                    borderRadius: '12px',
                    fontSize: '15px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    boxShadow: '0 4px 16px rgba(52,168,83,0.3)',
                  }}
                >
                  <FiCheckCircle
                    size={18}
                    style={{ marginRight: '8px', verticalAlign: 'middle' }}
                  />
                  Recibi mi pedido
                </button>
              )}
              {pedido.lat_delivery && pedido.lng_delivery && (
                <div style={{ marginTop: '12px' }}>
                  <MapaUbicacion
                    lat={pedido.lat_delivery}
                    lng={pedido.lng_delivery}
                    readOnly={true}
                    height="160px"
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {modalAlerta && (
          <div
            onClick={() => setModalAlerta('')}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2000,
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                backgroundColor: 'var(--color-surface)',
                borderRadius: '16px',
                padding: '24px',
                maxWidth: '320px',
                width: '90%',
                boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                textAlign: 'center',
              }}
            >
              <FiXCircle size={36} color="var(--color-danger)" style={{ marginBottom: '12px' }} />
              <div
                style={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: 'var(--color-text)',
                  marginBottom: '16px',
                }}
              >
                {modalAlerta}
              </div>
              <button
                onClick={() => setModalAlerta('')}
                style={{
                  padding: '10px 28px',
                  backgroundColor: 'var(--color-secondary)',
                  color: 'var(--color-surface)',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Entendido
              </button>
            </div>
          </div>
        )}

        {pedido.lat_delivery && pedido.lng_delivery && (
          <div
            style={{
              margin: '12px 0',
              borderRadius: '14px',
              overflow: 'hidden',
              border: '2px solid var(--color-secondary)',
            }}
          >
            <div
              style={{
                backgroundColor: 'var(--color-secondary)',
                color: 'var(--color-surface)',
                padding: '8px 14px',
                fontSize: '13px',
                fontWeight: 600,
              }}
            >
              <FiTruck size={14} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
              Delivery en camino
            </div>
            <MapaUbicacion
              lat={pedido.lat_delivery}
              lng={pedido.lng_delivery}
              readOnly={true}
              height="200px"
            />
          </div>
        )}

        {config?.requiere_ubicacion_cliente !== false && pedido.direccion_entrega && (
          <div style={S.card}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '8px',
              }}
            >
              <h4
                style={{
                  fontSize: '14px',
                  fontWeight: 700,
                  color: 'var(--color-primary)',
                  margin: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                <FiMapPin size={15} /> Dirección de entrega
              </h4>
              {!esTerminado && (
                <button
                  onClick={abrirCambiarDireccion}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '10px',
                    border: '1px solid var(--color-secondary)',
                    backgroundColor: 'transparent',
                    color: 'var(--color-secondary)',
                    fontSize: '12px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                  }}
                >
                  <FiEdit3 size={12} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                  Cambiar
                </button>
              )}
            </div>
            <div style={{ fontSize: '13px', color: 'var(--color-text)', lineHeight: 1.4 }}>
              {pedido.direccion_entrega}
            </div>
            {pedido.lat_entrega && pedido.lng_entrega && (
              <div style={{ marginTop: 8, borderRadius: 10, overflow: 'hidden' }}>
                <MapaUbicacion
                  lat={pedido.lat_entrega}
                  lng={pedido.lng_entrega}
                  readOnly
                  height="140px"
                />
              </div>
            )}
          </div>
        )}

        {pedido.estado !== 'pagado' && pedido.estado !== 'esperando_pago' && !esTerminado && (
          <div style={S.card}>
            <h4
              style={{
                fontSize: '14px',
                fontWeight: 700,
                color: 'var(--color-primary)',
                margin: '0 0 10px',
              }}
            >
              Articulos ({productos.length})
            </h4>
            {productos.map((prod, i) => {
              const precioBase = prod.pesable
                ? (prod.precio * (prod.cantidad || 1)) / 1000
                : prod.precio * (prod.cantidad || 1);
              const etiqExtra = (prod.etiquetas_extra || 0) ||
                (Array.isArray(prod.etiquetas) ? prod.etiquetas.reduce((s, e) => s + (e.precio || 0), 0) : 0);
              const subtotal = precioBase + etiqExtra;
              const imgSrc = getImagenUrl(prod.imagen_url);
              const esPendiente = pedido.estado === 'pendiente'
                || (pedido.estado === 'en_revision' && config?.cliente_puede_modificar_en_revision);
              const etiquetas = Array.isArray(prod.etiquetas) ? prod.etiquetas : [];
              return (
                <div
                  key={i}
                  onClick={
                    esPendiente ? () => setProductoSeleccionado({ ...prod, index: i }) : undefined
                  }
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '10px',
                    padding: '10px 0',
                    borderBottom: i < productos.length - 1 ? '1px solid var(--color-bg)' : 'none',
                    cursor: esPendiente ? 'pointer' : 'default',
                    opacity: esPendiente ? 1 : 0.7,
                  }}
                >
                  {imgSrc ? (
                    <img
                      src={imgSrc}
                      alt={prod.nombre}
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: 10,
                        objectFit: 'cover',
                        flexShrink: 0,
                        marginTop: 2,
                      }}
                      onError={(e) => (e.target.style.display = 'none')}
                    />
                  ) : (
                    <FiPackage size={20} color="var(--color-secondary)" style={{ flexShrink: 0, marginTop: 4 }} />
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: '13px', lineHeight: 1.3 }}>{prod.nombre}</div>
                    <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)', marginTop: '2px' }}>
                      {prod.pesable
                        ? `${(prod.cantidad / 1000).toFixed(2)} kg`
                        : `${prod.cantidad} und`}
                      {' '}x ${fmtMonto(prod.precio)}
                      {' '}= ${fmtMonto(precioBase)}
                    </div>
                    {etiquetas.length > 0 && (
                      <div style={{ marginTop: '5px', paddingLeft: '2px' }}>
                        {etiquetas.map(et => (
                          <div key={et.id || et.nombre} style={{
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            fontSize: '11px', padding: '3px 0',
                          }}>
                            <span style={{
                              color: 'var(--color-text-secondary)',
                              display: 'flex', alignItems: 'center', gap: '4px',
                            }}>
                              <span style={{
                                display: 'inline-block', width: '4px', height: '4px',
                                borderRadius: '50%', backgroundColor: 'var(--color-secondary)',
                                flexShrink: 0,
                              }} />
                              {et.nombre}
                            </span>
                            {et.precio > 0 && (
                              <span style={{
                                fontWeight: 600, color: 'var(--color-secondary)',
                                marginLeft: '12px', flexShrink: 0,
                              }}>
                                +${fmtMonto(et.precio)}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    {prod.combo_id && Array.isArray(prod.componentes) && prod.componentes.length > 0 && (
                      <div style={{ marginTop: '6px', paddingLeft: '2px' }}>
                        {prod.componentes.map((comp, ci) => {
                          const compEtiq = Array.isArray(comp.etiquetas) ? comp.etiquetas : [];
                          return (
                            <div key={ci} style={{
                              display: 'flex', alignItems: 'center', gap: '5px',
                              fontSize: '11px', padding: '2px 0',
                            }}>
                              <span style={{
                                display: 'inline-block', width: '4px', height: '4px',
                                borderRadius: '50%', backgroundColor: '#f0b400',
                                flexShrink: 0,
                              }} />
                              <span style={{ color: 'var(--color-text-secondary)' }}>{comp.nombre}</span>
                              {compEtiq.length > 0 && (
                                <span style={{ color: 'var(--color-text-muted)' }}>
                                  ({compEtiq.map((e) => e.nombre).join(', ')})
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <span
                    style={{
                      fontWeight: 700, color: 'var(--color-secondary)',
                      fontSize: '13px', flexShrink: 0, marginTop: 2,
                    }}
                  >
                    ${fmtMonto(subtotal)}
                  </span>
                </div>
              );
            })}
            {(pedido?.costo_envio || 0) > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: '13px', color: 'var(--color-text-secondary)' }}>
                <span>Costo de envío</span>
                <span style={{ fontWeight: 600 }}>${fmtMonto(pedido.costo_envio)}</span>
              </div>
            )}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '10px 0 0',
                marginTop: '6px',
                borderTop: '2px solid var(--color-border)',
                fontWeight: 700,
                fontSize: '15px',
              }}
            >
              <span>Total</span>
              <span style={{ color: 'var(--color-secondary)' }}>${fmtMonto(total)}</span>
            </div>
          </div>
        )}
      </main>

      <div style={{ ...S.footer, ...(inline ? { position: 'sticky', bottom: 0 } : {}) }}>
        {pedido.estado === 'pendiente' && (
          <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
            <button
              onClick={() => setConfirmarPedido(true)}
              style={{
                flex: 1,
                padding: '12px',
                borderRadius: '16px',
                border: 'none',
                backgroundColor: 'var(--color-success)',
                color: 'var(--color-surface)',
                fontSize: '14px',
                fontWeight: 700,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
              }}
            >
              <FiCheckCircle size={16} /> Confirmar pedido
            </button>
            <button
              onClick={() => setConfirmarCancelar(true)}
              style={{
                flex: 1,
                padding: '12px',
                borderRadius: '16px',
                border: '1px solid var(--color-danger)',
                backgroundColor: '#FFF0EF',
                color: 'var(--color-danger)',
                fontSize: '14px',
                fontWeight: 700,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
              }}
            >
              <FiXCircle size={16} /> Cancelar pedido
            </button>
          </div>
        )}
        {!esTerminado && pedido.session_id && (
          <button
            onClick={() => (onGoToChat ? onGoToChat() : navigate(`/chat/${pedido.session_id}`))}
            style={S.chatBtn}
          >
            <FiMessageSquare size={16} /> Ir al chat del pedido
          </button>
        )}
        {(pedido.estado === 'pendiente'
          || (pedido.estado === 'en_revision' && config?.cliente_puede_modificar_en_revision)) && (
          <button
            onClick={() => {
              localStorage.setItem(
                `agorax_pedido_catalogo_temp_${config?.slug || ''}`,
                JSON.stringify({
                  id: pedido.id,
                  session_id: pedido.session_id,
                  productos: pedido.productos,
                  codigo_sede: pedido.codigo_sede,
                })
              );
              onGoToCatalog ? onGoToCatalog() : navigate('/');
            }}
            style={{
              ...S.chatBtn,
              backgroundColor: 'var(--color-success)',
              marginTop: pedido.session_id ? '8px' : '0',
            }}
          >
            <FiGrid size={16} /> Ir al catálogo
          </button>
        )}
        {!esTerminado && pedido.estado !== 'pendiente' &&
          !(pedido.estado === 'en_revision' && config?.cliente_puede_modificar_en_revision) && (
          <p
            style={{
              fontSize: '11px',
              color: '#FF9500',
              textAlign: 'center',
              margin: '8px 0 0',
              fontWeight: 600,
            }}
          >
            No se pueden modificar los productos en este estado.
          </p>
        )}
        {esTerminado && (
          <p
            style={{
              fontSize: '11px',
              color: 'var(--color-text-secondary)',
              textAlign: 'center',
              margin: '8px 0 0',
            }}
          >
            Pedido finalizado.
          </p>
        )}
      </div>

      {/* Modal confirmar cancelacion */}
      {confirmarCancelar && (
        <div onClick={() => setConfirmarCancelar(false)} style={M.overlay}>
          <div onClick={(e) => e.stopPropagation()} style={M.modal}>
            <div style={M.modalHeader}>
              <h3
                style={{
                  margin: 0,
                  fontSize: '17px',
                  fontWeight: 700,
                  color: 'var(--color-primary)',
                }}
              >
                Cancelar pedido
              </h3>
              <button onClick={() => setConfirmarCancelar(false)} style={M.closeBtn}>
                <FiXCircle size={20} />
              </button>
            </div>
            <p
              style={{
                fontSize: '14px',
                color: 'var(--color-text-secondary)',
                margin: '0 0 20px',
                lineHeight: 1.5,
              }}
            >
              Estas seguro de que deseas cancelar el pedido? Esta
              acción no se puede deshacer.
            </p>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => setConfirmarCancelar(false)} style={M.secondaryBtn}>
                Volver
              </button>
              <button
                onClick={() => {
                  setConfirmarCancelar(false);
                  cancelarPedido();
                }}
                style={M.dangerBtn}
              >
                Si, cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal confirmar pedido */}
      {confirmarPedido && (
        <div onClick={() => setConfirmarPedido(false)} style={M.overlay}>
          <div onClick={(e) => e.stopPropagation()} style={{ ...M.modal, maxWidth: '400px' }}>
            <div style={M.modalHeader}>
              <h3
                style={{
                  margin: 0,
                  fontSize: '17px',
                  fontWeight: 700,
                  color: 'var(--color-primary)',
                }}
              >
                Confirmar pedido
              </h3>
              <button onClick={() => setConfirmarPedido(false)} style={M.closeBtn}>
                <FiXCircle size={20} />
              </button>
            </div>
            <p
              style={{ fontSize: '14px', color: 'var(--color-text-secondary)', margin: '0 0 12px' }}
            >
              Revisa los productos de tu pedido:
            </p>
            <div
              style={{
                backgroundColor: 'var(--color-bg)',
                borderRadius: '14px',
                padding: '12px',
                marginBottom: '16px',
                maxHeight: '200px',
                overflowY: 'auto',
              }}
            >
              {productos.map((p, i) => {
                const base = p.pesable
                  ? (p.precio * (p.cantidad || 1)) / 1000
                  : p.precio * (p.cantidad || 1);
                const etiqExtra = p.etiquetas_extra || (p.etiquetas || []).reduce((t, e) => t + (e.precio || 0), 0);
                const st = base + etiqExtra;
                const imgSrc = getImagenUrl(p.imagen_url);
                return (
                  <div
                    key={i}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '6px 0',
                      borderBottom:
                        i < productos.length - 1 ? '1px solid var(--color-border)' : 'none',
                    }}
                  >
                    {imgSrc ? (
                      <img
                        src={imgSrc}
                        alt={p.nombre}
                        style={{
                          width: 28,
                          height: 28,
                          borderRadius: 6,
                          objectFit: 'cover',
                          flexShrink: 0,
                        }}
                        onError={(e) => (e.target.style.display = 'none')}
                      />
                    ) : (
                      <FiPackage
                        size={16}
                        color="var(--color-secondary)"
                        style={{ flexShrink: 0 }}
                      />
                    )}
                    <div style={{ flex: 1 }}>
                      <span style={{ fontSize: '12px', color: 'var(--color-primary)' }}>
                        {p.nombre}
                      </span>
                      {etiqExtra > 0 && (
                        <div style={{ fontSize: '10px', color: '#ff6d00', marginTop: '1px' }}>
                          +${fmtMonto(etiqExtra)} aditivo(s)
                        </div>
                      )}
                      {p.combo_id &&
                        Array.isArray(p.componentes) &&
                        p.componentes.length > 0 && (
                          <div style={{ marginTop: '2px', paddingLeft: '2px' }}>
                            {p.componentes.map((comp, ci) => {
                              const compEtiq = Array.isArray(comp.etiquetas) ? comp.etiquetas : [];
                              return (
                                <div
                                  key={ci}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    fontSize: '10px',
                                    padding: '1px 0',
                                  }}
                                >
                                  <span
                                    style={{
                                      display: 'inline-block',
                                      width: 4,
                                      height: 4,
                                      borderRadius: '50%',
                                      backgroundColor: '#f0b400',
                                      flexShrink: 0,
                                    }}
                                  />
                                  <span style={{ color: 'var(--color-text-secondary)' }}>
                                    {comp.nombre}
                                  </span>
                                  {compEtiq.length > 0 && (
                                    <span style={{ color: 'var(--color-text-muted)' }}>
                                      ({compEtiq.map((e) => e.nombre).join(', ')})
                                    </span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                    </div>
                    <span style={{ fontSize: '11px', color: 'var(--color-text-secondary)' }}>
                      x{p.cantidad}
                    </span>
                    <span
                      style={{ fontSize: '12px', fontWeight: 600, color: 'var(--color-secondary)' }}
                    >
                      ${fmtMonto(st)}
                    </span>
                  </div>
                );
              })}
            </div>
            {(pedido?.costo_envio || 0) > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: '13px', color: 'var(--color-text-secondary)' }}>
                <span>Costo de envío</span>
                <span style={{ fontWeight: 600 }}>${fmtMonto(pedido.costo_envio)}</span>
              </div>
            )}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '15px',
                fontWeight: 700,
                marginBottom: '16px',
              }}
            >
              <span>Total</span>
              <span style={{ color: 'var(--color-secondary)' }}>${fmtMonto(total)}</span>
            </div>
            {tiposDelivery.length > 0 && (
              <div style={{ marginBottom: '16px' }}>
                <div
                  style={{
                    fontSize: '13px',
                    fontWeight: 700,
                    color: 'var(--color-primary)',
                    marginBottom: '8px',
                  }}
                >
                  Método de entrega
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {tiposDelivery.map((t) => {
                    const TIcon = TIPO_ENTREGA_ICON[t] || FiBox;
                    const sel = tipoEntrega === t;
                    return (
                      <button
                        key={t}
                        onClick={() => setTipoEntrega(t)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          padding: '12px',
                          borderRadius: '12px',
                          cursor: 'pointer',
                          textAlign: 'left',
                          border: sel
                            ? '2px solid var(--color-secondary)'
                            : '1px solid var(--color-border)',
                          backgroundColor: sel ? 'var(--color-bg-hover)' : 'var(--color-surface)',
                          color: sel ? 'var(--color-secondary)' : 'var(--color-text)',
                        }}
                      >
                        <TIcon size={18} />
                        <span style={{ fontSize: '14px', fontWeight: 600 }}>
                          {TIPO_ENTREGA_LABEL[t] || t}
                        </span>
                        {sel && <FiCheckCircle size={16} style={{ marginLeft: 'auto' }} />}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => setConfirmarPedido(false)} style={M.secondaryBtn}>
                Volver
              </button>
              <button
                onClick={ejecutarConfirmar}
                disabled={tiposDelivery.length > 0 && !tipoEntrega}
                style={{
                  ...M.primaryBtn,
                  backgroundColor: 'var(--color-success)',
                  opacity: tiposDelivery.length > 0 && !tipoEntrega ? 0.6 : 1,
                }}
              >
                <FiCheckCircle size={16} style={{ marginRight: '6px' }} />
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal detalle de producto */}
      {productoSeleccionado &&
        (() => {
          const p = productoSeleccionado;
          const imgSrc = getImagenUrl(p.imagen_url);
          const precio = p.precio || 0;
          const precioBs = p.precio_bs || precio * tasa;
          const cant = p.cantidad || 1;
          const esPesable = p.pesable || false;
          const stockDisponible = stockCache[p.codigo] ?? 0;
          const stockMax = esPesable ? stockDisponible * 1000 : stockDisponible;
          const etiquetas = p.etiquetas || [];
          const etiqExtraUnit = etiquetas.reduce((t, e) => t + (e.precio || 0), 0);
          const subtotal = (esPesable ? (precio * cant) / 1000 : precio * cant) + etiqExtraUnit * cant;
          const subtotalBs = (esPesable ? (precioBs * cant) / 1000 : precioBs * cant) + etiqExtraUnit * cant * tasa;
          return (
            <div onClick={() => setProductoSeleccionado(null)} style={M.overlay}>
              <div onClick={(e) => e.stopPropagation()} style={{ ...M.modal, maxWidth: '380px', maxHeight: '90vh', overflowY: 'auto' }}>
                <div style={M.modalHeader}>
                  <h3
                    style={{
                      margin: 0,
                      fontSize: '17px',
                      fontWeight: 700,
                      color: 'var(--color-primary)',
                    }}
                  >
                    {p.nombre}
                  </h3>
                  <button onClick={() => setProductoSeleccionado(null)} style={M.closeBtn}>
                    <FiXCircle size={20} />
                  </button>
                </div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '14px',
                    marginBottom: '16px',
                  }}
                >
                  {imgSrc ? (
                    <img
                      src={imgSrc}
                      alt={p.nombre}
                      style={{
                        width: 80,
                        height: 80,
                        borderRadius: 14,
                        objectFit: 'cover',
                        flexShrink: 0,
                      }}
                      onError={(e) => (e.target.style.display = 'none')}
                    />
                  ) : (
                    <div
                      style={{
                        width: 80,
                        height: 80,
                        borderRadius: 14,
                        backgroundColor: 'var(--color-bg)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <FiPackage size={32} color="var(--color-secondary)" />
                    </div>
                  )}
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        fontSize: '13px',
                        color: 'var(--color-text-secondary)',
                        marginBottom: '4px',
                      }}
                    >
                      {esPesable ? `${(cant / 1000).toFixed(2)} kg` : `${cant} und`} x $
                      {fmtMonto(precio)}
                    </div>
                    <div
                      style={{ fontSize: '18px', fontWeight: 800, color: 'var(--color-secondary)' }}
                    >
                      ${fmtMonto(subtotal)}
                    </div>
                    {etiqExtraUnit > 0 && (
                      <div style={{ fontSize: '11px', color: '#ff6d00', marginTop: '2px' }}>
                        +${fmtMonto(etiqExtraUnit)}/und en aditivos{' '}
                        {cant > 1 ? `(${fmtMonto(etiqExtraUnit * cant)} total)` : ''}
                      </div>
                    )}
                    {tasa > 0 && (
                      <div
                        style={{
                          fontSize: '14px',
                          color: 'var(--color-text-secondary)',
                          marginTop: '2px',
                        }}
                      >
                        Bs {fmtMonto(subtotalBs)}
                      </div>
                    )}
                    {!pva && stockDisponible !== undefined && stockDisponible !== null && (
                      <div style={{ fontSize: '11px', marginTop: '4px', fontWeight: 600,
                        color: stockDisponible > 10 ? 'var(--color-success)' : stockDisponible > 0 ? '#FF9500' : 'var(--color-danger)'
                      }}>
                        {stockDisponible > 0
                          ? `Stock: ${esPesable ? `${stockDisponible.toFixed(1)} kg` : stockDisponible} und`
                          : 'Sin stock'}
                      </div>
                    )}
                  </div>
                </div>

                {/* Etiquetas editor */}
                {p._etiquetasDisponibles && p._etiquetasDisponibles.length > 0 && (
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--color-primary)', marginBottom: '8px' }}>
                      Aditivos / Extras
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                      {p._etiquetasDisponibles.map((et) => {
                        const seleccionada = etiquetas.some((sel) => sel.id === et.id);
                        return (
                          <button
                            key={et.id}
                            onClick={() => {
                              const nuevasEtiq = seleccionada
                                ? etiquetas.filter((sel) => sel.id !== et.id)
                                : [...etiquetas, { id: et.id, nombre: et.nombre, precio: et.precio || 0 }];
                              const nuevoExtra = nuevasEtiq.reduce((t, e) => t + (e.precio || 0), 0);
                              setProductoSeleccionado({
                                ...p,
                                etiquetas: nuevasEtiq,
                                etiquetas_extra: nuevoExtra,
                              });
                            }}
                            style={{
                              padding: '6px 12px',
                              borderRadius: '16px',
                              border: seleccionada ? '2px solid #ff6d00' : '1px solid var(--color-border)',
                              backgroundColor: seleccionada ? '#fff3e0' : 'var(--color-surface)',
                              color: seleccionada ? '#e65100' : 'var(--color-text-secondary)',
                              fontSize: '12px',
                              fontWeight: seleccionada ? 700 : 500,
                              cursor: 'pointer',
                              fontFamily: 'inherit',
                              transition: 'all 0.15s',
                            }}
                          >
                            {seleccionada ? '✓ ' : '+ '}
                            {et.nombre}
                            {et.precio > 0 ? ` ($${fmtMonto(et.precio)})` : ''}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Etiquetas actuales como fallback si no hay disponibles */}
                {(!p._etiquetasDisponibles || p._etiquetasDisponibles.length === 0) && etiquetas.length > 0 && (
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--color-primary)', marginBottom: '8px' }}>
                      Aditivos actuales
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                      {etiquetas.map((et) => (
                        <span key={et.id} style={{
                          padding: '4px 10px', borderRadius: '12px',
                          backgroundColor: '#fff3e0', color: '#e65100',
                          fontSize: '11px', fontWeight: 600,
                        }}>
                          {et.nombre}{et.precio > 0 ? ` (+$${fmtMonto(et.precio)})` : ''}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    marginBottom: '16px',
                  }}
                >
                  <button
                    onClick={() => {
                      const step = esPesable ? 50 : 1;
                      if (cant > step) {
                        const nuevo = { ...p, cantidad: cant - step };
                        setProductoSeleccionado(nuevo);
                      }
                    }}
                    disabled={cant <= (esPesable ? 50 : 1)}
                    style={{ ...M.qtyBtn, opacity: cant <= (esPesable ? 50 : 1) ? 0.3 : 1 }}
                  >
                    <FiMinus size={16} />
                  </button>
                  <span
                    style={{
                      fontSize: '18px',
                      fontWeight: 700,
                      minWidth: '60px',
                      textAlign: 'center',
                    }}
                  >
                    {esPesable ? `${(cant / 1000).toFixed(3)} kg` : cant}
                  </span>
                  <button
                    onClick={() => {
                      const step = esPesable ? 50 : 1;
                      const max = pva ? cant + step : (stockDisponible > 0 ? Math.min(cant + step, stockMax) : cant + step);
                      const nuevo = { ...p, cantidad: max };
                      setProductoSeleccionado(nuevo);
                    }}
                    disabled={!pva && (stockDisponible <= 0 || cant >= stockMax)}
                    style={{ ...M.qtyBtn, opacity: (!pva && (stockDisponible <= 0 || cant >= stockMax)) ? 0.3 : 1 }}
                  >
                    <FiPlus size={16} />
                  </button>
                  <button
                    onClick={async () => {
                      try {
                        const nuevos = productos.filter((_, i) => i !== p.index);
                        const nuevoTotal = nuevos.reduce(
                          (s, x) => s + (x.pesable ? (x.precio * (x.cantidad || 1)) / 1000 : x.precio * (x.cantidad || 1)) + etiquetaTotal(x),
                          0
                        );
                        await axios.put(`${API_URL}/pedidos/${pedido.id}/sync-cart`, {
                          schema_name: schema_name,
                          productos: nuevos,
                          total: nuevoTotal,
                        });
                        if (pedido.session_id) {
                          localStorage.setItem(
                            `agorax_cart_${pedido.session_id}`,
                            JSON.stringify(nuevos.map((n) => ({ codigo: n.codigo, nombre: n.nombre, precio: n.precio || 0, precio_bs: 0, cantidad: n.cantidad || 1, stock: 0, pesable: n.pesable, imagen_url: n.imagen_url, etiquetas: n.etiquetas, etiquetas_extra: n.etiquetas_extra, combo_id: n.combo_id || null, componentes: n.componentes || null })))
                          );
                        }
                        setProductoSeleccionado(null);
                        cargarPedido();
                      } catch (e) {
                        console.error('Error:', e);
                        const msg = e?.response?.data?.message || 'Error al eliminar producto';
                        agregarNotificacion({ tipo: 'error', mensaje: msg, duracion: 5000 });
                      }
                    }}
                    style={{
                      ...M.qtyBtn,
                      borderColor: '#FFCDD2',
                      color: 'var(--color-danger)',
                      marginLeft: '8px',
                    }}
                  >
                    <FiTrash2 size={16} />
                  </button>
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button onClick={() => setProductoSeleccionado(null)} style={M.secondaryBtn}>
                    Salir
                  </button>
                  <button
                    onClick={async () => {
                      try {
                        const nuevos = productos.map((prod, i) =>
                          i === p.index ? { ...prod, cantidad: cant, etiquetas: etiquetas, etiquetas_extra: prod.combo_id ? (prod.etiquetas_extra || 0) : etiqExtraUnit * cant } : prod
                        );
                        const nuevoTotal = nuevos.reduce(
                          (s, x) => s + (x.pesable ? (x.precio * (x.cantidad || 1)) / 1000 : x.precio * (x.cantidad || 1)) + etiquetaTotal(x),
                          0
                        );
                        await axios.put(`${API_URL}/pedidos/${pedido.id}/sync-cart`, {
                          schema_name: schema_name,
                          productos: nuevos,
                          total: nuevoTotal,
                        });
                        if (pedido.session_id) {
                          const cartKey = `agorax_cart_${pedido.session_id}`;
                          const existing = localStorage.getItem(cartKey);
                          let cart = existing ? JSON.parse(existing) : [];
                          if (Array.isArray(cart) && cart.length > 0) {
                            cart = cart.map((item) => {
                              const match = nuevos.find(
                                (n) => n.codigo === item.codigo
                              );
                              return match
                                ? {
                                    ...item,
                                    cantidad: match.cantidad,
                                    precio: match.precio,
                                    pesable: match.pesable,
                                    etiquetas: match.etiquetas,
                                    etiquetas_extra: match.etiquetas_extra,
                                  }
                                : item;
                            });
                          } else {
                            cart = nuevos.map((n) => ({ codigo: n.codigo, nombre: n.nombre, precio: n.precio || 0, precio_bs: 0, cantidad: n.cantidad || 1, stock: 0, pesable: n.pesable, imagen_url: n.imagen_url, etiquetas: n.etiquetas, etiquetas_extra: n.etiquetas_extra, combo_id: n.combo_id || null, componentes: n.componentes || null }));
                          }
                          localStorage.setItem(cartKey, JSON.stringify(cart));
                        }
                        setProductoSeleccionado(null);
                        cargarPedido();
                      } catch (e) {
                        console.error('Error:', e);
                        const msg = e?.response?.data?.message || 'Error al modificar producto';
                        agregarNotificacion({ tipo: 'error', mensaje: msg, duracion: 5000 });
                      }
                    }}
                    style={{ ...M.primaryBtn, backgroundColor: 'var(--color-secondary)' }}
                  >
                    <FiCheckCircle size={16} style={{ marginRight: '4px' }} /> Confirmar
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

      {mostrarCambiarDir && (
        <div onClick={() => setMostrarCambiarDir(false)} style={M.overlay}>
          <div onClick={(e) => e.stopPropagation()} style={{ ...M.modal, maxWidth: '400px' }}>
            <div style={M.modalHeader}>
              <h3
                style={{
                  margin: 0,
                  fontSize: '17px',
                  fontWeight: 700,
                  color: 'var(--color-primary)',
                }}
              >
                Cambiar dirección de entrega
              </h3>
              <button onClick={() => setMostrarCambiarDir(false)} style={M.closeBtn}>
                <FiXCircle size={20} />
              </button>
            </div>
            {cargandoDirLista ? (
              <div style={{ textAlign: 'center', padding: '24px 0' }}>
                <div
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    border: '3px solid var(--color-border)',
                    borderTopColor: 'var(--color-secondary)',
                    animation: 'spin 0.8s linear infinite',
                    margin: '0 auto 8px',
                  }}
                />
                <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
                  Cargando direcciones...
                </span>
              </div>
            ) : direccionesCliente.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <FiMapPin size={32} color="var(--color-text-muted)" style={{ marginBottom: 8 }} />
                <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', margin: 0 }}>
                  No tienes direcciones guardadas con ubicación.
                </p>
              </div>
            ) : (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                  maxHeight: '320px',
                  overflowY: 'auto',
                  marginBottom: 12,
                }}
              >
                {direccionesCliente.map((dir) => {
                  const esActual =
                    pedido.direccion_entrega === dir.direccion ||
                    (pedido.lat_entrega === dir.lat && pedido.lng_entrega === dir.lng);
                  return (
                    <button
                      key={dir.id}
                      onClick={() => !esActual && ejecutarCambiarDireccion(dir.id)}
                      disabled={esActual || cambiandoDir}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        padding: 12,
                        borderRadius: 14,
                        cursor: esActual ? 'default' : 'pointer',
                        textAlign: 'left',
                        border: esActual
                          ? '2px solid var(--color-success)'
                          : '1px solid var(--color-border)',
                        backgroundColor: esActual ? '#f0fdf4' : 'var(--color-surface)',
                        color: esActual ? 'var(--color-success)' : 'var(--color-text)',
                        opacity: cambiandoDir && !esActual ? 0.6 : 1,
                      }}
                    >
                      <FiMapPin size={18} style={{ flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>
                          {dir.nombre || 'Sin nombre'}
                          {esActual ? ' (actual)' : ''}
                        </div>
                        <div
                          style={{
                            fontSize: 11,
                            color: 'var(--color-text-secondary)',
                            marginTop: 2,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {dir.direccion}
                        </div>
                      </div>
                      {cambiandoDir && !esActual && (
                        <div
                          style={{
                            width: 16,
                            height: 16,
                            borderRadius: '50%',
                            border: '2px solid var(--color-border)',
                            borderTopColor: 'var(--color-secondary)',
                            animation: 'spin 0.8s linear infinite',
                          }}
                        />
                      )}
                    </button>
                  );
                })}
              </div>
            )}
            <button
              onClick={() => setMostrarCambiarDir(false)}
              style={{
                width: '100%',
                padding: '10px',
                borderRadius: '12px',
                border: '1px solid var(--color-border)',
                backgroundColor: 'var(--color-surface)',
                color: 'var(--color-text-secondary)',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              Cancelar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const S = {
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    height: '100dvh',
    maxWidth: '900px',
    margin: '0 auto',
    backgroundColor: 'var(--color-bg)',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  header: {
    backgroundColor: 'var(--color-primary)',
    color: 'var(--color-surface)',
    padding: '12px 16px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flexShrink: 0,
    minHeight: '52px',
  },
  backBtn: {
    background: 'none',
    border: 'none',
    color: 'var(--color-surface)',
    cursor: 'pointer',
    padding: 0,
    display: 'flex',
  },
  content: { flex: 1, overflowY: 'auto', padding: '16px', minHeight: 0 },
  timeline: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0 0 14px',
    gap: '4px',
    borderBottom: '1px solid var(--color-border)',
    marginBottom: '8px',
  },
  card: {
    backgroundColor: 'var(--color-surface)',
    borderRadius: '20px',
    padding: '14px 16px',
    boxShadow: '0 1px 3px rgba(11,26,74,0.04)',
    marginTop: '12px',
  },
  footer: {
    padding: '12px 16px',
    borderTop: '1px solid var(--color-border)',
    backgroundColor: 'var(--color-surface)',
    flexShrink: 0,
  },
  chatBtn: {
    width: '100%',
    padding: '14px',
    backgroundColor: 'var(--color-secondary)',
    color: 'var(--color-surface)',
    border: 'none',
    borderRadius: '16px',
    fontSize: '14px',
    fontWeight: 700,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    boxShadow: '0 4px 16px rgba(41,82,204,0.25)',
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100dvh',
    color: 'var(--color-text-secondary)',
    fontSize: '15px',
  },
};

const M = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: 'var(--color-surface)',
    borderRadius: '20px',
    padding: '24px',
    maxWidth: '340px',
    width: '90%',
    boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: 'var(--color-text-secondary)',
    padding: '4px',
  },
  secondaryBtn: {
    flex: 1,
    padding: '12px',
    borderRadius: '12px',
    border: '1px solid var(--color-border)',
    backgroundColor: 'var(--color-surface)',
    color: 'var(--color-text-secondary)',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  dangerBtn: {
    flex: 1,
    padding: '12px',
    borderRadius: '12px',
    border: 'none',
    backgroundColor: 'var(--color-danger)',
    color: 'var(--color-surface)',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  primaryBtn: {
    flex: 1,
    padding: '12px',
    borderRadius: '12px',
    border: 'none',
    color: 'var(--color-surface)',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  qtyBtn: {
    width: 34,
    height: 34,
    borderRadius: 10,
    border: '1px solid var(--color-border)',
    backgroundColor: 'var(--color-bg)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
};

export default ClientePedidoDetalle;
